import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PREST0_ORGANIZATION_REF } from '../config/shellStructuredData';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
// PromptBar imports removed per Miles 2026-04-06
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
import ProfileImage from '../components/ProfileImage';
import MobileNotifications from '../components/MobileNotifications';
import NotificationsBell from '../components/NotificationsBell';
import db from '../utils/firestore';
// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(2deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px) translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(-50%);
  }
`;

const slideInMobile = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Page Container
const BlogContainer = styled.div`
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  background: var(--brand-canvas);
  position: relative;
  padding-bottom: clamp(3rem, 6vh, 5rem);
  display: flex;
  flex-direction: column;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding-bottom: clamp(3.5rem, 6vh, 5rem);
  }
  
  @media (max-width: 480px) {
    padding-bottom: clamp(3rem, 5vh, 4rem);
  }
`;

// Hero section
const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  color: white;
  padding: clamp(7.5rem, 13vh, 9rem) 2rem clamp(3.5rem, 7vh, 4.5rem);
  text-align: left;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 40%, rgba(198, 167, 94, 0.08) 0%, transparent 40%),
                radial-gradient(circle at 70% 60%, rgba(198, 167, 94, 0.05) 0%, transparent 40%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: clamp(2.6rem, 8vh, 3.6rem) 1.35rem clamp(1.9rem, 4.6vh, 2.35rem);
    padding-top: calc(clamp(2.6rem, 8vh, 3.6rem) + env(safe-area-inset-top));
  }

  @media (max-width: 480px) {
    padding: clamp(2.4rem, 9.5vh, 3.2rem) 1.15rem clamp(1.75rem, 4.2vh, 2.2rem);
    padding-top: calc(clamp(2.4rem, 9.5vh, 3.2rem) + env(safe-area-inset-top));
  }
`;

const BlogHeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out;
  padding-left: 0;
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const HeroInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(2rem, 4vw, 3rem);
  color: #FFFFFF;
  padding: 0 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
  }
`;

const HeroLeft = styled.div`
  flex: 1.1;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 2.5vw, 1.75rem);
  align-items: center;
  text-align: center;
  margin: 0 auto;
  justify-content: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
  max-width: 520px;
  text-align: center;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
`;

const HeroCTA = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--brand-gold);
  color: var(--brand-text);
  font-weight: 600;
  padding: 0.75rem 1.35rem;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 12px 24px rgba(198, 167, 94, 0.25);
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 30px rgba(198, 167, 94, 0.22);
  }
`;

const HeroSecondary = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  padding: 0.7rem 1.25rem;
  border-radius: 999px;
  border: 1px solid rgba(198, 167, 94, 0.35);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(198, 167, 94, 0.15);
    transform: translateY(-1px);
  }
`;

const HeroTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.25rem;
  justify-content: center;
`;

const HeroTag = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(198, 167, 94, 0.2);
    color: var(--brand-gold);
  }
`;

const HeroRight = styled.div`
  flex: 0.9;
  display: flex;
  align-items: stretch;
`;

const HeroCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: clamp(1.75rem, 3vw, 2.25rem);
  color: var(--brand-text);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.12);
  position: relative;
  overflow: hidden;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(140deg, rgba(198, 167, 94, 0.06), rgba(198, 167, 94, 0.03));
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const HeroCardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.8rem;
  background: rgba(198, 167, 94, 0.12);
  color: var(--brand-gold);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: lowercase;
`;

const HeroCardTitle = styled.h3`
  font-family: 'Satoshi', sans-serif;
  font-size: clamp(1.5rem, 2.5vw, 1.85rem);
  line-height: 1.35;
  font-weight: 700;
  color: var(--brand-text);
  margin: 0;
`;

const HeroCardMeta = styled.span`
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const HeroCardExcerpt = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  margin: 0;
`;

const HeroCardCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HeroCardCategory = styled.button.attrs({ type: 'button' })`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--brand-gold);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(198, 167, 94, 0.14);
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(198, 167, 94, 0.22);
    color: var(--brand-gold-hover);
    transform: translateY(-1px);
  }
`;

const HeroCardLink = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  border: none;
  background: none;
  color: var(--brand-gold);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateX(4px);
  }
`;

const NewsletterOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(17, 17, 17, 0.65);
  display: ${props => (props.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  backdrop-filter: blur(5px);
  z-index: 200;
  animation: ${fadeIn} 0.25s ease-out;
`;

const NewsletterModal = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  width: min(440px, 100%);
  padding: 2.25rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const NewsletterModalTitle = styled.h3`
  font-family: 'Satoshi', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--brand-text);
  text-transform: lowercase;
  margin: 0;
`;

const NewsletterModalText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  margin: 0;
  text-transform: lowercase;
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 0.5rem;
`;

const NewsletterInput = styled.input`
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &::placeholder {
    color: var(--brand-text-secondary);
    text-transform: lowercase;
  }

  &[type="email"] {
    text-transform: lowercase;
  }
`;

const NewsletterSubmit = styled.button.attrs({ type: 'submit' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand-gold);
  color: var(--brand-text);
  font-weight: 600;
  padding: 0.8rem 1.25rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  text-transform: lowercase;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  margin-top: 0.25rem;

  &:hover {
    transform: translateY(-2px);
    background: var(--brand-gold-hover);
    box-shadow: 0 14px 26px rgba(198, 167, 94, 0.3);
  }
`;

const NewsletterClose = styled.button.attrs({ type: 'button' })`
  position: absolute;
  top: 1.15rem;
  right: 1.15rem;
  border: none;
  background: none;
  color: var(--brand-text-secondary);
  cursor: pointer;
  display: inline-flex;
  padding: 0.3rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--brand-text);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BlogTitle = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.4rem, 3.8vw, 3rem);
  font-weight: 700;
  margin-bottom: 2rem;
  letter-spacing: -0.02em;
  text-transform: lowercase;
  line-height: 1.1;
  text-align: center;

  @media (max-width: 768px) {
    font-size: clamp(2.1rem, 7vw, 2.5rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(2rem, 7.4vw, 2.3rem);
  }
`;

// Blog content section
const BlogContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 2.5rem;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    padding-bottom: 3rem;
    display: flex;
    flex-direction: column;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    padding-bottom: 3rem;
    width: 100%;
  }
`;

// Main content area
const MainContent = styled.div`
  animation: ${fadeIn} 0.8s ease-out;
  order: 1;
  
  @media (max-width: 768px) {
    order: 1;
  }
`;

// Sidebar area
const Sidebar = styled.div`
  animation: ${fadeIn} 1s ease-out;
  order: 2;
  
  @media (max-width: 1024px) and (min-width: 769px) {
    order: 2;
  }
  
  @media (max-width: 768px) {
    margin-top: 3rem;
    order: 2;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -1.5rem;
      left: 0;
      width: 100%;
      height: 1px;
      background: linear-gradient(to right, rgba(0,0,0,0.05), rgba(0,0,0,0.1), rgba(0,0,0,0.05));
    }
  }
`;

// Blog post components
const BlogPostsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
`;

const BlogPost = styled.article`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  }
`;

const FeaturedBlogPost = styled(BlogPost)`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogPostImage = styled.div<{ $imageSrc?: string }>`
  width: 100%;
  height: 240px;
  background: ${props => props.$imageSrc ? `url(${props.$imageSrc})` : 'var(--brand-canvas)'};
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  
  ${props => !props.$imageSrc && css`
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, var(--brand-canvas) 0%, var(--brand-line) 20%, var(--brand-canvas) 40%, var(--brand-canvas) 100%);
      animation: ${shimmer} 2s infinite linear;
      background-size: 800px 100%;
    }
  `}
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const FeaturedPostImage = styled(BlogPostImage)`
  height: 100%;
  
  @media (max-width: 768px) {
    height: 240px;
  }
`;

const BlogPostContent = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
  }
`;

const BlogPostCategory = styled.div`
  font-size: 0.85rem;
  color: var(--brand-gold);
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: lowercase;
  letter-spacing: 0.03em;
`;

// Add new component for multiple categories
const BlogPostCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    gap: 0.4rem;
    margin-bottom: 0.75rem;
  }
`;

const BlogPostCategoryTag = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 30px;
  background: rgba(198, 167, 94, 0.1);
  color: var(--brand-gold);
  text-transform: lowercase;
  letter-spacing: 0.03em;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(198, 167, 94, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
  }
`;

const BlogPostTitle = styled.h2`
  font-family: 'Satoshi', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.4;
  color: var(--brand-text);
  text-transform: lowercase;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const BlogPostExcerpt = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  margin-bottom: 1.5rem;
  text-transform: lowercase;
`;

const BlogPostMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const BlogPostDate = styled.span`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }
`;

const BlogPostReadMore = styled(Link)`
  color: var(--brand-gold);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-transform: lowercase;
  
  &:hover {
    color: var(--brand-gold-hover);
  }
  
  svg {
    margin-left: 0.5rem;
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

// Featured blog post with larger image
const FeaturedBlogPostContent = styled(BlogPostContent)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

// Blog post interactions component
const BlogPostInteractions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--brand-line);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 0.75rem;
    padding-top: 1.2rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-top: 1.2rem;
    padding-top: 1.2rem;
  }
`;

const InteractionButton = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  color: ${props => props.$active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)'};
  font-size: 0.9rem;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &.liked-animation svg {
    animation: likeAnimation 0.6s ease-in-out;
  }
  
  @keyframes likeAnimation {
    0% { transform: scale(1); }
    50% { transform: scale(1.4); }
    100% { transform: scale(1); }
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.5rem 0.7rem;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Sidebar components
const SidebarSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    width: 100%;
    box-sizing: border-box;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-radius: 12px;
  }
`;

const SidebarTitle = styled.h3`
  font-family: 'Satoshi', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--brand-text);
  position: relative;
  text-transform: lowercase;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--brand-gold);
    border-radius: 3px;
  }
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const CategoryItem = styled.li`
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const CategoryLink = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  color: var(--brand-text-secondary);
  font-size: 1rem;
  transition: all 0.2s ease;
  padding: 0.5rem 0;
  text-transform: lowercase;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  
  &:hover {
    color: var(--brand-gold);
  }
  
  span:last-child {
    background: transparent;
    padding: 0.25rem 0.5rem;
    border-radius: 20px;
    font-size: 0.8rem;
    color: var(--brand-text-secondary);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    background: var(--brand-canvas);
    border-radius: 30px;

    &:hover {
      background: var(--brand-line);
    }
  }
`;

const PopularPostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PopularPost = styled(Link)`
  display: flex;
  gap: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const PopularPostImage = styled.div<{ $imageSrc?: string; $hasVideo?: boolean }>`
  width: 70px;
  height: 70px;
  border-radius: 10px;
  background: ${props => props.$imageSrc ? `url(${props.$imageSrc})` : 'var(--brand-canvas)'};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, filter 0.2s ease;
  
  ${props => !props.$imageSrc && css`
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, var(--brand-canvas) 0%, var(--brand-line) 20%, var(--brand-canvas) 40%, var(--brand-canvas) 100%);
      animation: ${shimmer} 2s infinite linear;
      background-size: 800px 100%;
    }
  `}

  ${props => props.$hasVideo && props.$imageSrc && css`
    filter: saturate(1.05) brightness(0.88);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(0, 0, 0, 0.32), rgba(0, 0, 0, 0.05));
    }

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-38%, -50%);
      width: 0;
      height: 0;
      border-top: 9px solid transparent;
      border-bottom: 9px solid transparent;
      border-left: 15px solid rgba(255, 255, 255, 0.92);
      filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.35));
    }
  `}
`;

const PopularPostContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const PopularPostTitle = styled.h4`
  font-family: 'Satoshi', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  line-height: 1.4;
  text-transform: lowercase;
`;

const PopularPostMeta = styled.div`
  font-size: 0.8rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const SubscribeForm = styled.form`
  margin-top: 1.5rem;
`;

const SubscribeInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--brand-line);
  font-size: 0.95rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  text-transform: lowercase;
  
  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.1);
  }
  
  &::placeholder {
    text-transform: lowercase;
  }
`;

const SubscribeButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  background: var(--brand-gold);
  color: var(--brand-text);
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: lowercase;
  
  &:hover {
    background: var(--brand-gold-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3rem;
  text-transform: lowercase;
  gap: 0.5rem;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${props => props.$active ? 'var(--brand-gold)' : 'white'};
  color: ${props => props.$active ? 'var(--brand-text)' : 'var(--brand-text-secondary)'};
  font-weight: 600;
  border: 1px solid ${props => props.$active ? 'transparent' : 'var(--brand-line)'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? 'var(--brand-gold-hover)' : 'var(--brand-canvas)'};
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--brand-canvas);
    border-color: var(--brand-line);
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// Navigation sidebar component
const NavigationBar = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
  padding: 1.25rem 0.65rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px 0 0 10px;
  transition: all 0.3s ease;
  overflow: hidden;
  width: 130px;
  animation: ${slideIn} 0.8s ease-out;
  
  &:hover {
    box-shadow: 0 8px 24px rgba(198, 167, 94, 0.08);
  }
  
  @media (max-width: 1400px) {
    top: auto;
    right: 50%;
    bottom: clamp(3.5rem, 6vh, 4.75rem);
    transform: translateX(50%);
    flex-direction: row;
    align-items: center;
    justify-content: center;
    overflow-x: auto;
    width: auto;
    padding: 0.85rem 1.5rem;
    border-radius: 12px;
    animation: ${slideInMobile} 0.8s ease-out;
    z-index: 1400;
    gap: 0.25rem;
  }

  @media (max-width: 768px) {
    top: auto;
    right: 50%;
    bottom: 4.5rem;
    transform: translateX(50%);
    flex-direction: row;
    overflow-x: auto;
    width: auto;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    animation: ${slideInMobile} 0.8s ease-out;
    z-index: 1400;
  }
  
  @media (max-width: 480px) {
    bottom: 5rem;
    padding: 0.6rem 1rem;
    width: auto;
    max-width: 90%;
  }
`;

const NavItem = styled.a<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: ${props => props.$active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)'};
  font-size: 0.7rem;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  text-transform: lowercase;
  
  svg {
    width: 22px;
    height: 22px;
    margin-bottom: 0.5rem;
    opacity: ${props => props.$active ? 1 : 0.7};
    transition: all 0.3s ease;
  }
  
  &:hover {
    color: var(--brand-gold);
    
    svg {
      opacity: 1;
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 1400px) {
    flex-direction: row;
    align-items: center;
    padding: 0.6rem 0.9rem;
    font-size: 0.75rem;
    
    svg {
      width: 20px;
      height: 20px;
      margin-bottom: 0;
      margin-right: 0.5rem;
    }

    &:hover svg {
      transform: translateX(-2px);
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    
    svg {
      margin-right: 0.5rem;
    }
  }
`;

// Empty state component
const EmptyState = styled.div`
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  
  svg {
    width: 80px;
    height: 80px;
    color: var(--brand-line);
    margin-bottom: 1.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: var(--brand-text);
    text-transform: lowercase;
  }
  
  p {
    font-size: 1rem;
    color: var(--brand-text-secondary);
    margin-bottom: 2rem;
    max-width: 500px;
    text-transform: lowercase;
  }
  
  button {
    padding: 0.875rem 1.5rem;
    border-radius: 8px;
    background: var(--brand-gold);
    color: var(--brand-text);
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: lowercase;

    &:hover {
      background: var(--brand-gold-hover);
      transform: translateY(-2px);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    }
  }
`;

const SubscriptionText = styled.p`
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  color: var(--brand-text-secondary);
  line-height: 1.6;
  text-transform: lowercase;
`;

// Add HomeHeader for the top-right corner navigation (copied from TeamExpertise.tsx)
const HomeHeader = styled.header`
  position: fixed;
  top: 2.5rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    top: 1.75rem;
    right: 1.5rem;
    gap: 0.8rem;
    display: none; // Hide the header on mobile, it will be replaced by the mobile menu
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    color: var(--brand-text);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    span {
      display: none; // Hide text on mobile
    }
    
    svg {
      margin-right: 0;
    }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  
  &:hover {
    color: var(--brand-text);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

// Mobile menu components
const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 8px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-text);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;
    
    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '4px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }
    
    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '14px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  padding: 1rem 0;
  z-index: 200000; /* stay above prompt bars and overlays */
  flex-direction: column;
  width: 180px;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.2s ease-out;
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-charcoal);
  text-decoration: none;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  font-weight: 500;
  
  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
    
    svg {
      opacity: 1;
    }
  }
`;

const MobileMenuButton = styled.button`
  background: var(--brand-gold);
  color: var(--brand-text);
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 8px 12px 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: var(--brand-gold-hover);
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MobileMenuLogoutButton = styled.button`
  color: #ff4d4f;
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;
  
  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }
  
  &:hover {
    background: rgba(255, 77, 79, 0.05);
    color: #ff1f23;
    
    svg {
      opacity: 1;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// Blog post content components
const BlogFullPost = styled.article`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-bottom: 2rem;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
  }
`;

const BlogPostHeader = styled.div`
  padding: 2.5rem 2.5rem 0.5rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem 0.5rem;
  }
`;

const BlogPostHeading = styled.h1`
  font-family: 'Satoshi', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 1.5rem;
  line-height: 1.3;
  text-transform: none;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    line-height: 1.25;
  }
`;

const BlogPostSubheading = styled.h2`
  font-family: 'Satoshi', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--brand-charcoal);
  margin: 2rem 0 1rem;
  line-height: 1.4;
  text-transform: none;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
    margin: 1.5rem 0 0.75rem;
  }
`;

const BlogPostSection = styled.div`
  padding: 0 2.5rem 1.5rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem 1rem;
  }
`;

const BlogPostText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--brand-charcoal);
  margin-bottom: 1.5rem;
  text-transform: none;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

const Callout = styled.div`
  margin: 2.5rem 0;
  padding: clamp(1.5rem, 3vw, 2rem);
  border-radius: 16px;
  background: linear-gradient(140deg, rgba(198, 167, 94, 0.08), rgba(26, 26, 26, 0.04));
  border: 1px solid rgba(198, 167, 94, 0.2);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
`;

const CalloutTitle = styled.h4`
  font-family: 'Satoshi', sans-serif;
  margin: 0 0 0.75rem;
  font-size: clamp(1.05rem, 2vw, 1.3rem);
  font-weight: 700;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const CalloutText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: var(--brand-charcoal);
`;

const BlogPostList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  
  li {
    font-size: 1.05rem;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    color: var(--brand-charcoal);
    text-transform: none;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
      padding-right: 0.5rem;
    }
  }
`;

const BlogPostTable = styled.div`
  overflow-x: auto;
  margin: 2rem 0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 480px) {
    margin: 1.5rem -0.5rem;
    width: calc(100% + 1rem);
    border-radius: 8px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-transform: none;
  font-size: 0.95rem;
  table-layout: fixed;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--brand-line);
    word-wrap: break-word;
  }
  
  th {
    background-color: var(--brand-canvas);
    font-weight: 600;
    color: var(--brand-text);
  }
  
  tbody tr:nth-child(even) {
    background-color: var(--brand-canvas);
  }
  
  tbody tr:hover {
    background-color: rgba(198, 167, 94, 0.05);
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    
    th, td {
      padding: 0.75rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    
    th, td {
      padding: 0.6rem;
    }
    
    th:first-child, td:first-child {
      width: 40%;
    }
  }
`;

const BlogPostImageContainer = styled.div`
  margin: 2rem 0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const BlogPostImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
  max-width: 100%;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const BlogPostCaption = styled.figcaption`
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  text-align: center;
  padding: 0.75rem 1rem;
  background: var(--brand-canvas);
  font-style: italic;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: var(--brand-line);
  margin: 2.5rem 0;
`;

const Citation = styled.div`
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--brand-line);
  text-transform: none;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const CitationList = styled.ol`
  margin-top: 0.5rem;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    word-break: break-word;
    padding-right: 0.5rem;
    
    a {
      color: var(--brand-gold);
      text-decoration: none;
      word-break: break-all;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    @media (max-width: 480px) {
      font-size: 0.8rem;
      margin-bottom: 0.75rem;
    }
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  padding: 1.5rem;
  background: var(--brand-canvas);
  border-radius: 10px;
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const AuthorAvatar = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-right: 1rem;
`;

const AuthorDetails = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 0.25rem;
`;

const AuthorBio = styled.div`
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  line-height: 1.6;
`;

const BlogPostHeaderDate = styled.div`
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    width: 16px;
    height: 16px;
  }
`;

// Add new component for author info at the top of post
const TopAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TopAuthorAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 0.75rem;
`;

const TopAuthorDetails = styled.div`
  flex: 1;
`;

const TopAuthorName = styled.div`
  font-weight: 600;
  color: var(--brand-text);
  font-size: 0.95rem;
  line-height: 1.4;
`;

const TopAuthorRole = styled.div`
  font-size: 0.8rem;
  color: var(--brand-text-secondary);
  line-height: 1.4;
`;

// Add styled components for social sharing
const ShareOptionsContainer = styled.div`
  position: relative;
  display: inline-block;
  
  @media (max-width: 480px) {
    position: static;
  }
`;

const ShareButton = styled.button`
  background: none;
  border: none;
  color: var(--brand-text-secondary);
  font-size: 0.9rem;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.4rem;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ShareMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  display: ${props => props.isOpen ? 'grid' : 'none'};
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  z-index: 100;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px;
    border-style: solid;
    border-color: white transparent transparent transparent;
  }
  
  @media (max-width: 480px) {
    width: 240px;
    grid-template-columns: repeat(3, 1fr);
    bottom: 45px;
    right: 0;
    left: auto;
    transform: none;
    
    &::after {
      left: auto;
      right: 15px;
      transform: none;
    }
  }
`;

const SocialShareButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const FacebookButton = styled(SocialShareButton)`
  background: #3b5998;
  &:hover {
    background: #344e86;
  }
`;

const TwitterButton = styled(SocialShareButton)`
  background: #1da1f2;
  &:hover {
    background: #0d95e8;
  }
`;

const LinkedinButton = styled(SocialShareButton)`
  background: #0077b5;
  &:hover {
    background: #00669c;
  }
`;

const RedditButton = styled(SocialShareButton)`
  background: #ff4500;
  &:hover {
    background: #e03d00;
  }
`;

const WhatsappButton = styled(SocialShareButton)`
  background: #25d366;
  &:hover {
    background: #20bd5a;
  }
`;

const EmailButton = styled(SocialShareButton)`
  background: #505050;
  &:hover {
    background: #404040;
  }
`;

const CopyLinkButton = styled(SocialShareButton)`
  background: #6c757d;
  &:hover {
    background: #5a6268;
  }
`;

const TelegramButton = styled(SocialShareButton)`
  background: #0088cc;
  &:hover {
    background: #0077b3;
  }
`;

// Add styled component for viewing full post
const FullPostLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--brand-gold);
  color: var(--brand-text);
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  text-transform: lowercase;
  
  &:hover {
    background: var(--brand-gold-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.2);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.6rem 1.25rem;
  }
`;

// Add notification/toast component
const Notification = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: ${props => props.show ? '80px' : '-100px'};
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 500;
  z-index: 9999;
  opacity: ${props => props.show ? 1 : 0};
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.6rem 1rem;
    bottom: ${props => props.show ? '90px' : '-100px'};
  }
`;

// PromptBar styled components removed per Miles 2026-04-06
// GlobalChatSystem will be added to this page later.

// Main Blog component
const Blog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const [activeSection, setActiveSection] = useState('latest');
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Add state for bookmarking functionality
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  // Add state for full post view
  
  // Add state for sharing functionality
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const shareMenuRef = useRef<HTMLDivElement>(null);

  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  
  // Add this state variable near the other state declarations in the Blog component
  const [currentSharedPostId, setCurrentSharedPostId] = useState<string>('');
  const [pendingFocusPostId, setPendingFocusPostId] = useState<string | null>(null);

  const clearInsightsHash = useCallback(() => {
    if (window.location.pathname.startsWith('/insights')) {
      window.history.replaceState({}, '', '/insights');
    }
    setCurrentSharedPostId('');
    setPendingFocusPostId(null);
  }, [setCurrentSharedPostId, setPendingFocusPostId]);

  const handleMobileNavClick = useCallback(() => {
    clearInsightsHash();
    setIsMobileMenuOpen(false);
  }, [clearInsightsHash, setIsMobileMenuOpen]);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Simulate loading posts
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingPosts(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Add click outside handler for mobile menu and share menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element;
      
      // Handle mobile menu
      if (isMobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
      
      // Handle share menu
      if (isShareMenuOpen && shareMenuRef.current && !shareMenuRef.current.contains(target as Node) && 
          !target.closest('.share-button')) {
        setIsShareMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen, isShareMenuOpen]);
  
  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showCopyNotification) {
      const timer = setTimeout(() => {
        setShowCopyNotification(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [showCopyNotification]);
  
  // Scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(sectionId);
  };

  const scrollToPostElement = (postId: string) => {
    const element = document.getElementById(postId);
    if (!element) {
      return false;
    }

    const yOffset = -100;
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveSection('latest');
    setCurrentSharedPostId(postId);
    window.history.replaceState({}, '', `/insights#${postId}`);
    return true;
  };
  
  const openNewsletterModal = (prefillEmail?: string) => {
    if (prefillEmail) {
      setNewsletterEmail(prefillEmail);
    } else {
      setNewsletterEmail('');
    }
    setNewsletterName('');
    setIsNewsletterModalOpen(true);
    setActiveSection('subscribe');
  };

  // Handle subscribe
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = subscriberEmail.trim();
    openNewsletterModal(trimmedEmail || undefined);
    setSubscriberEmail('');
  };

  const handleHeroPrimaryClick = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
    }
    scrollToSection('latest');
  };

  const handleHeroSecondaryClick = () => {
    openNewsletterModal();
  };

  const handleHeroCardClick = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
    }
    if (featuredHeroPost.slug) {
      focusPost(featuredHeroPost.slug);
    }
  };

  const focusPost = (postId: string) => {
    const didFocus = scrollToPostElement(postId);
    if (!didFocus) {
      setPendingFocusPostId(postId);
    }
  };

  useEffect(() => {
    if (pendingFocusPostId && !loadingPosts) {
      const didFocus = scrollToPostElement(pendingFocusPostId);
      if (didFocus) {
        setPendingFocusPostId(null);
      }
    }
  }, [pendingFocusPostId, loadingPosts, selectedCategory]);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Newsletter subscribed: ${newsletterEmail}`);
    setNewsletterEmail('');
    setNewsletterName('');
    setIsNewsletterModalOpen(false);
  };
  
  // Add handleLogin function for the floating header bar
  const handleLogin = () => {
    // Navigate to auth page with insights as the return URL
    console.log('Navigating to auth page with insights return URL');
    clearInsightsHash();
    navigate('/auth', { state: { returnUrl: '/insights' } });
  };
  
  // Handle post interactions
  const handleLike = (postId: string) => {
    if (isAuthenticated) {
      // Toggle like animation here
      const likeButton = document.querySelector(`.like-button-${postId}`);
      if (likeButton) {
        likeButton.classList.add('liked-animation');
        setTimeout(() => {
          likeButton.classList.remove('liked-animation');
        }, 1000);
      }
      
      setNotificationMessage(`You liked the post!`);
      setShowCopyNotification(true);
    } else {
    clearInsightsHash();
      navigate('/'); // Redirect to login
    }
  };
  
const VideoEmbedContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  margin: 2.5rem 0;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 18px;
  }

  @media (max-width: 768px) {
    padding-bottom: 60%;
    border-radius: 14px;
    iframe {
      border-radius: 14px;
    }
  }

  @media (max-width: 480px) {
    padding-bottom: 65%;
    border-radius: 12px;
    iframe {
      border-radius: 12px;
    }
  }
`;

  const handleComment = (postId: string) => {
    if (isAuthenticated) {
      // Show comment section or open comment modal
      // For now, show a notification
      setNotificationMessage('Comment feature coming soon!');
      setShowCopyNotification(true);
      
      // Scroll to comment section if exists
      const commentSection = document.getElementById(`comments-${postId}`);
      if (commentSection) {
        commentSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      clearInsightsHash();
      navigate('/'); // Redirect to login
    }
  };
  
  const handleBookmark = (postId: string) => {
    setIsBookmarked(!isBookmarked);
    if (isAuthenticated) {
      const action = !isBookmarked ? 'bookmarked' : 'removed bookmark from';
      setNotificationMessage(`Post ${action}!`);
      setShowCopyNotification(true);
    } else {
      clearInsightsHash();
      navigate('/'); // Redirect to login
    }
  };
  
  const handleShare = (postId: string) => {
    setCurrentSharedPostId(postId);
    setIsShareMenuOpen(!isShareMenuOpen);
  };
  
  // Function to handle viewing the full post
  const handleViewFullPost = (postId: string) => {
    focusPost(postId);
  };
  
  // Helper function to get post title by ID
  const getPostTitleById = (postId: string): string => {
    if (postId === 'ai-mass-layoff-reckoning') {
      return 'The AI Mass Layoff Reckoning';
    }
    if (postId === 'is-ai-in-a-bubble-part-2') {
      return 'Is AI In A Bubble? Part 2 - Time To Prepare Is NOW';
    }
    if (postId === 'is-ai-in-a-bubble') {
      return 'Is AI In A Bubble? (Time To Prepare Is NOW)';
    }
    if (postId === 'ai-agents-will-replace-you-unless-you-do-this') {
      return 'AI Agents Will Replace You Unless You Do This';
    }
    if (postId === 'ai-infrastructure-bottleneck') {
      return 'The AI Infrastructure Bottleneck Keeping Me Up at Night';
    }
    if (postId === 'ai-agent-security-crisis-nobody-talking-about') {
      return "The #1 Security Crisis Nobody's Talking About";
    }
    if (postId === 'ai-agent-opportunity-nobody-talking-about-yet') {
      return 'The AI Agent Opportunity Nobody Is Talking About Yet';
    }
    if (postId === 'agentic-ai-window-closing-fast') {
      return "The Agentic AI Window Just Opened. It's Closing Faster Than You Think.";
    }
    if (postId === 'secret-hidden-ai-revolution') {
      return 'The Secret About AI Agents Hidden in Plain Sight';
    }
    if (postId === 'claude-skills-enterprise-inflection') {
      return 'The Enterprise AI Agent Inflection Point Nobody\'s Talking About';
    }
    if (postId === 'ai-agent-inflection-point-video') {
      return 'The AI Agent Inflection Point (Video Spotlight)';
    }
    if (postId === 'mem0-memory-infrastructure') {
      return 'why agent memory infrastructure unlocks your largest opportunity';
    }
    if (postId === 'paypal-agentic-commerce-inflection') {
      return "PayPal's Agentic Commerce Inflection Point";
    }
    if (postId === 'ai-agent-revolution') {
      return 'The Value of the Coming AI Agent Revolution';
    }
    if (postId === 'ai-agents-current-trends') {
      return 'The State of AI Agents and Current Trends';
    }
    // Default title if post ID is not found
    return 'Blog Post';
  };
  
  // Share functionality
  const getShareUrl = () => {
    const origin = window.location.origin;
    const postAnchor = currentSharedPostId || 'ai-mass-layoff-reckoning';
    return `${origin}/insights#${postAnchor}`;
  };
  
  const handleCopyLink = () => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url)
      .then(() => {
        setNotificationMessage('Link copied to clipboard!');
        setShowCopyNotification(true);
        setIsShareMenuOpen(false);
      })
      .catch(err => {
        console.error('Could not copy link: ', err);
        setNotificationMessage('Failed to copy link');
        setShowCopyNotification(true);
      });
  };
  
  const handleShareFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const postTitle = getPostTitleById(currentSharedPostId || 'ai-mass-layoff-reckoning');
    const text = encodeURIComponent(`${postTitle} - A fascinating read on the future of AI!`);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareLinkedin = () => {
    const url = encodeURIComponent(getShareUrl());
    const postTitle = getPostTitleById(currentSharedPostId || 'ai-mass-layoff-reckoning');
    const title = encodeURIComponent(postTitle);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareReddit = () => {
    const url = encodeURIComponent(getShareUrl());
    const postTitle = getPostTitleById(currentSharedPostId || 'ai-mass-layoff-reckoning');
    const title = encodeURIComponent(postTitle);
    window.open(`https://www.reddit.com/submit?url=${url}&title=${title}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareWhatsapp = () => {
    const url = encodeURIComponent(getShareUrl());
    const postTitle = getPostTitleById(currentSharedPostId || 'ai-mass-layoff-reckoning');
    const text = encodeURIComponent(`${postTitle} - A fascinating read on the future of AI!`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareTelegram = () => {
    const url = encodeURIComponent(getShareUrl());
    const postTitle = getPostTitleById(currentSharedPostId || 'ai-mass-layoff-reckoning');
    const text = encodeURIComponent(`${postTitle} - A fascinating read on the future of AI!`);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const handleShareEmail = () => {
    const url = getShareUrl();
    const postTitle = getPostTitleById(currentSharedPostId || 'is-ai-in-a-bubble');
    const subject = encodeURIComponent(postTitle);
    const body = encodeURIComponent(`I thought you might find this article interesting: ${postTitle}\n\n${url}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setIsShareMenuOpen(false);
  };
  
  const getAssetPath = (assetPath: string) => `${process.env.PUBLIC_URL || ''}${assetPath}`;
  // Photorealistic image of traders analyzing market data on glowing displays, capturing urgency around AI investment cycles
  const IS_AI_BUBBLE_IMAGE = 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const IS_AI_BUBBLE_VIDEO_URL = 'https://www.youtube.com/embed/DI1M1tGZgEQ?si=sr58UkguKXPA9m2u';
  // Photorealistic aerial of a financial district at dusk, highlighting macro shifts for part 2 analysis
  const IS_AI_BUBBLE_PART2_IMAGE = 'https://images.unsplash.com/photo-1520607162513-c6a5bc94ff81?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const IS_AI_BUBBLE_PART2_VIDEO_URL = 'https://www.youtube.com/embed/hx4qb5Bzclg?si=ux1_7K6j_kobcG12';
  // Photorealistic scene of a modern office floor with empty desks and soft evening light, symbolizing workforce transitions
  const AI_MASS_LAYOFF_IMAGE = 'https://images.unsplash.com/photo-1521737604893-0d7fd0b0aa12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const AI_MASS_LAYOFF_VIDEO_URL = 'https://www.youtube.com/embed/mDaGJUzDafk?si=AT3XZg1LurvcgBEv';
  // Photorealistic image of a founder reviewing AI dashboards in a glass-walled operations room
  const AI_AGENTS_REPLACE_YOU_IMAGE = 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const AI_AGENTS_REPLACE_YOU_VIDEO_URL = 'https://www.youtube.com/embed/KgjeRUw2kPw?si=sW1S3BtCu2sUngVY';
  // Photorealistic image of power grid infrastructure - electrical transmission towers and power lines representing energy distribution
  const AI_INFRASTRUCTURE_BOTTLENECK_IMAGE = 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const AI_AGENT_SECURITY_IMAGE = 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const AI_AGENT_OPPORTUNITY_IMAGE = 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  const PAYPAL_AGENTIC_IMAGE = getAssetPath('/images/blog/paypal-agentic-commerce.webp');
  const AI_AGENT_VIDEO_IMAGE = getAssetPath('/images/blog/ai-agent-inflection-point.webp');
  const MEM0_IMAGE = getAssetPath('/images/blog/mem0-memory-infrastructure.webp');
  const CLAUDE_SKILLS_IMAGE = getAssetPath('/images/blog/claude-skills-enterprise-inflection.webp');
  const SECRET_HIDDEN_IMAGE = getAssetPath('/images/blog/secret-hidden-ai-revolution.webp');
  const AGENTIC_WINDOW_IMAGE = 'https://img.youtube.com/vi/LHtaVMOQ_Qo/hqdefault.jpg';
  // Using image showing ChatGPT/AI interface on phone - specifically relevant to ChatGPT Apps SDK and agent marketplace
  const AI_AGENT_APP_STORE_IMAGE = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  // Using image showing business/IPO/stock market - relevant to OpenAI's $1T IPO
  const OPENAI_IPO_IMAGE = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80';
  
  const allBlogPosts = [
    {
      id: 'ai-mass-layoff-reckoning',
      title: 'the AI mass layoff reckoning',
      date: 'november 10, 2025',
      categories: ['artificial intelligence', 'future of work', 'business strategy', 'video spotlight']
    },
    {
      id: 'is-ai-in-a-bubble-part-2',
      title: 'is ai in a bubble? part 2 - time to prepare is now',
      date: 'november 8, 2025',
      categories: ['artificial intelligence', 'market analysis', 'business strategy', 'video spotlight']
    },
    {
      id: 'is-ai-in-a-bubble',
      title: 'is ai in a bubble? (time to prepare is now)',
      date: 'november 8, 2025',
      categories: ['artificial intelligence', 'market analysis', 'business strategy', 'video spotlight']
    },
    {
      id: 'ai-agents-will-replace-you-unless-you-do-this',
      title: 'ai agents will replace you unless you do this',
      date: 'november 6, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'business strategy', 'video spotlight']
    },
    {
      id: 'ai-infrastructure-bottleneck',
      title: 'the AI infrastructure bottleneck keeping me up at night',
      date: 'november 6, 2025',
      categories: ['artificial intelligence', 'agent infrastructure', 'energy infrastructure', 'market analysis']
    },
    {
      id: 'ai-agent-security-crisis-nobody-talking-about',
      title: "the #1 security crisis nobody's talking about",
      date: 'november 5, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'cybersecurity', 'identity security']
    },
    {
      id: 'ai-agent-opportunity-nobody-talking-about-yet',
      title: 'the AI agent opportunity nobody is talking about yet',
      date: 'november 4, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'business opportunity', 'video spotlight']
    },
    {
      id: 'agentic-ai-window-closing-fast',
      title: "the agentic AI window just opened. it's closing faster than you think.",
      date: 'november 2, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'market analysis', 'future of work']
    },
    {
      id: 'openai-trillion-dollar-ipo-starting-gun',
      title: 'OpenAI\'s $1 trillion ipo just fired the starting gun',
      date: 'november 1, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'market analysis', 'founder insights']
    },
    {
      id: 'ai-agent-app-store-phase',
      title: 'the AI agent revolution just entered the "app store" phase',
      date: 'october 31, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'market analysis', 'founder insights']
    },
    {
      id: 'secret-hidden-ai-revolution',
      title: 'the secret about ai agents hidden in plain sight',
      date: 'october 30, 2025',
      categories: ['artificial intelligence', 'agent engineering', 'business opportunity', 'founder insights']
    },
    {
      id: 'claude-skills-enterprise-inflection',
      title: 'the enterprise AI agent inflection point nobody\'s talking about',
      date: 'october 29, 2025',
      categories: ['artificial intelligence', 'enterprise AI', 'agent engineering', 'market analysis']
    },
    {
      id: 'mem0-memory-infrastructure',
      title: 'why agent memory infrastructure unlocks your largest opportunity',
      date: 'october 29, 2025',
      categories: ['artificial intelligence', 'agent infrastructure', 'memory systems', 'funding news']
    },
    {
      id: 'ai-agent-inflection-point-video',
      title: 'the AI agent inflection point (video spotlight)',
      date: 'october 28, 2025',
      categories: ['artificial intelligence', 'video spotlight', 'founder insights', 'agent engineering']
    },
    {
      id: 'paypal-agentic-commerce-inflection',
      title: "paypal's agentic commerce inflection point",
      date: 'october 28, 2025',
      categories: ['artificial intelligence', 'agentic commerce', 'payments', 'technology']
    },
    {
      id: 'ai-agents-current-trends',
      title: 'the state of AI agents and current trends',
      date: 'may 14, 2025',
      categories: ['artificial intelligence', 'technology', 'market analysis']
    },
    {
      id: 'ai-agent-revolution',
      title: 'the value of the coming AI agent revolution',
      date: 'may 12, 2025',
      categories: ['artificial intelligence', 'technology', 'future of work']
    }
  ];

  const featuredHeroPost = {
    slug: 'ai-mass-layoff-reckoning',
    title: 'the AI mass layoff reckoning',
    date: 'november 10, 2025',
    excerpt: 'Rumors of mass AI layoffs are everywhere. Here’s what the data actually shows—and how founders, leaders, and builders can navigate the next wave of automation.',
    categories: ['artificial intelligence', 'future of work', 'business strategy', 'video spotlight']
  };

  const latestInsightStructuredData = {
    "@context": 'https://schema.org',
    "@type": 'BlogPosting',
    headline: 'The AI Mass Layoff Reckoning',
    description: 'Miles Maggio examines whether an AI-driven mass layoff wave is real, clarifies the numbers behind the headlines, and outlines the strategies leaders must deploy to protect teams while scaling agent leverage.',
    image: [AI_MASS_LAYOFF_IMAGE],
    datePublished: '2025-11-10T09:00:00-08:00',
    dateModified: '2025-11-10T09:00:00-08:00',
    mainEntityOfPage: {
      "@type": 'WebPage',
      "@id": 'https://www.prest0.ai/insights#ai-mass-layoff-reckoning'
    },
    author: {
      "@type": 'Person',
      name: 'Miles Maggio',
      jobTitle: 'top voice, feat. speaker, and advisor on AI agent engineering'
    },
    // the shell's one Organization, pointed to (src/config/shellStructuredData.ts)
    publisher: PREST0_ORGANIZATION_REF,
    articleSection: ['artificial intelligence', 'future of work', 'business strategy', 'video spotlight'],
    keywords: ['AI layoffs', 'future of work', 'AI workforce strategy', 'agent adoption impact', 'prest0 insights', 'Miles Maggio']
  } as const;

  const heroQuickLinks = [
    { label: 'future of work', category: 'future of work' },
    { label: 'business strategy', category: 'business strategy' },
    { label: 'market analysis', category: 'market analysis' },
    { label: 'artificial intelligence', category: 'artificial intelligence' }
  ];

  const filteredPosts = selectedCategory
    ? allBlogPosts.filter(post => post.categories.includes(selectedCategory))
    : allBlogPosts;

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
    scrollToSection('latest');
  };

  return (
    <BlogContainer>
      <SeoHead
        title="The AI Mass Layoff Reckoning"
        tabTitle="The AI Mass Layoff Reckoning"
        description="prest0 separates fact from fear around AI-driven layoffs and gives founders and operators the playbook to protect teams while scaling intelligent automation."
        keywords="prest0 insights, enterprise legal AI insights, law firm AI strategy, legal technology trends, AI legal services analysis"
        ogImage={AI_MASS_LAYOFF_IMAGE}
        ogType="article"
        canonicalUrl="https://prest0.ai/insights"
        schemaType="Article"
        structuredData={latestInsightStructuredData}
      />
      {/* Floating Header Bar (copied from TeamExpertise.tsx) */}
      <HomeHeader>
        
        <HeaderIconLink to="/mission" onClick={clearInsightsHash}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
            <line x1="4" y1="22" x2="4" y2="15"></line>
          </svg>
          <span>mission</span>
        </HeaderIconLink>
        <HeaderIconLink to="/team" onClick={clearInsightsHash}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span>team</span>
        </HeaderIconLink>
        <HeaderIconLink to="/" onClick={clearInsightsHash}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <span>home</span>
        </HeaderIconLink>
        {isAuthenticated ? (
          <ProfileImage size={36} />
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            <span>login</span>
          </HeaderIconButton>
        )}
        <NotificationsBell />
      </HomeHeader>
      
      {/* Mobile Menu */}
      <MobileMenuToggle className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
        <MobileMenuIcon isOpen={isMobileMenuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>
      
      <MobileMenuDropdown className="mobile-menu" isOpen={isMobileMenuOpen}>
        
        <MobileMenuLink to="/team" onClick={handleMobileNavClick}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </MobileMenuLink>
        <MobileMenuLink to="/mission" onClick={handleMobileNavClick}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          mission
        </MobileMenuLink>
        {!location.pathname.startsWith('/insights') && (
          <MobileMenuLink to="/insights" onClick={handleMobileNavClick}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="2" x2="12" y2="6" />
              <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
              <line x1="9" y1="22" x2="15" y2="22" />
            </svg>
            insights
          </MobileMenuLink>
        )}
        
        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop" onClick={handleMobileNavClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M16 12h.01"/>
                <path d="M2 10h20"/>
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M16 12h.01" />
                  <path d="M2 10h20" />
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity" onClick={handleMobileNavClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            {!location.pathname.startsWith('/profile') && (
              <MobileMenuLink to="/profile" onClick={handleMobileNavClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                profile
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/settings" onClick={handleMobileNavClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              settings
            </MobileMenuLink>
            <MobileMenuLogoutButton onClick={() => {
      clearInsightsHash();
              setIsMobileMenuOpen(false);
              logout();
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileMenuLogoutButton>
            <MobileNotifications closeMenu={() => setIsMobileMenuOpen(false)} />
          </>
        ) : (
          <MobileMenuButton onClick={() => {
            handleMobileNavClick();
            navigate('/auth', { 
              state: { 
                returnUrl: '/insights' 
              } 
            });
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>
      
      {/* Hero Section */}
      <HeroSection>
        <BlogHeroContent>
          <HeroInner>
            <HeroLeft>
              <BlogTitle>
                cognitive synergy
              </BlogTitle>
              <HeroSubtitle>
                strategic insights for agent builders
              </HeroSubtitle>
              <HeroActions>
                <HeroCTA onClick={handleHeroPrimaryClick}>
                  latest insights
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </HeroCTA>
                <HeroSecondary onClick={handleHeroSecondaryClick}>
                  subscribe
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </HeroSecondary>
              </HeroActions>
              <HeroTagRow>
                {heroQuickLinks.map(link => (
                  <HeroTag 
                    key={link.category}
                    onClick={() => handleCategoryClick(link.category)}
                    style={{
                      background: selectedCategory === link.category
                        ? 'rgba(198, 167, 94, 0.3)'
                        : 'rgba(255, 255, 255, 0.08)',
                      color: selectedCategory === link.category ? 'var(--brand-gold)' : 'rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    {link.label}
                  </HeroTag>
                ))}
              </HeroTagRow>
            </HeroLeft>
            <HeroRight>
              <HeroCard>
                <HeroCardBadge>latest insight</HeroCardBadge>
                <HeroCardTitle>{featuredHeroPost.title}</HeroCardTitle>
                <HeroCardMeta>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  {featuredHeroPost.date}
                </HeroCardMeta>
                <HeroCardExcerpt>{featuredHeroPost.excerpt}</HeroCardExcerpt>
                <HeroCardCategories>
                  {featuredHeroPost.categories.map(category => (
                    <HeroCardCategory
                      key={category}
                      onClick={() => handleCategoryClick(category)}
                      aria-pressed={selectedCategory === category}
                      style={{
                        background: selectedCategory === category
                          ? 'rgba(198, 167, 94, 0.2)'
                          : 'rgba(198, 167, 94, 0.14)',
                        color: selectedCategory === category ? 'var(--brand-gold-hover)' : 'var(--brand-gold)'
                      }}
                    >
                      {category}
                    </HeroCardCategory>
                  ))}
                </HeroCardCategories>
                <HeroCardLink onClick={handleHeroCardClick}>
                  read
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </HeroCardLink>
              </HeroCard>
            </HeroRight>
          </HeroInner>
        </BlogHeroContent>
      </HeroSection>
      
      {/* Blog Content */}
      <BlogContent>
        <MainContent>
          <section id="latest">
            {loadingPosts ? (
              <BlogPostsContainer>
                {/* Placeholder for loading */}
                <FeaturedBlogPost>
                  <FeaturedPostImage />
                  <BlogPostContent>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>Loading...</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostTitle>Loading featured article...</BlogPostTitle>
                    <BlogPostExcerpt>Loading content...</BlogPostExcerpt>
                  </BlogPostContent>
                </FeaturedBlogPost>
                
                {[1, 2].map(i => (
                  <BlogPost key={i}>
                    <BlogPostImage />
                    <BlogPostContent>
                      <BlogPostCategories>
                        <BlogPostCategoryTag>Loading...</BlogPostCategoryTag>
                      </BlogPostCategories>
                      <BlogPostTitle>Loading article...</BlogPostTitle>
                      <BlogPostExcerpt>Loading content...</BlogPostExcerpt>
                    </BlogPostContent>
                  </BlogPost>
                ))}
              </BlogPostsContainer>
            ) : (
              <>
                {filteredPosts.map(post => {
                  if (post.id === 'ai-mass-layoff-reckoning') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - AI Mass Layoff Reckoning */}
                <BlogFullPost id="ai-mass-layoff-reckoning">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>future of work</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business strategy</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the AI mass layoff reckoning</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 10, 2025 at 9:00 am PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      every news feed is shouting about an imminent AI-driven layoff tsunami. the reality is nuanced: automation is rewriting job descriptions faster than it is eliminating headcount, but teams who ignore the shift will feel the shock first. this breakdown separates signal from noise and gives you an action plan.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        src={AI_MASS_LAYOFF_VIDEO_URL}
                        title="The AI Mass Layoff Reckoning"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      ></iframe>
                    </VideoEmbedContainer>

                    <BlogPostSubheading>what the numbers actually say</BlogPostSubheading>
                    <BlogPostList>
                      <li>automation-heavy orgs saw a 6% reduction in repetitive roles, but simultaneously opened 8% more positions in agent orchestration, data governance, and compliance.</li>
                      <li>enterprise ai budgets now earmark 35% of spend for reskilling and workflow redesign—up from 12% just one year ago.</li>
                      <li>companies that pair layoffs with retraining see productivity rebound within two quarters; blunt layoffs without reinvestment lag for 12+ months.</li>
                    </BlogPostList>

                    <Callout>
                      <CalloutTitle>layoffs aren’t inevitable—misalignment is</CalloutTitle>
                      <CalloutText>
                        the organizations getting hit hardest are the ones that deployed agents without aligning process, incentives, and policy. the reckoning is about governance, not just headcount.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>how to navigate what’s next</BlogPostSubheading>
                    <BlogPostList>
                      <li><strong>map critical tasks:</strong> classify every workflow as automate, augment, or human-only. redeploy teams toward high-context work before cuts.</li>
                      <li><strong>stand up redeployment squads:</strong> pair talent ops with product to spin up agent enablement roles—prompt engineers, agent QA, compliance stewards.</li>
                      <li><strong>communicate with receipts:</strong> publish dashboards showing freed hours, error reduction, and new opportunities so teams see the win.</li>
                      <li><strong>co-create policy:</strong> involve legal, HR, and frontline leads in real-time guardrail updates; it keeps trust high while change accelerates.</li>
                    </BlogPostList>

                    <BlogPostSubheading>if layoffs are unavoidable</BlogPostSubheading>
                    <BlogPostText>
                      sometimes restructuring is necessary. do it like a systems engineer: provide 3–6 month runway, offer reskilling stipends tied to agent operations, and build alumni networks for contract-to-hire work as new agent opportunities emerge.
                    </BlogPostText>

                    <BlogPostText>
                      the future belongs to leaders who treat AI agents as force multipliers for people, not replacements. manage the transition with data, empathy, and transparency, and you will keep your best operators while scaling automation responsibly.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #FutureOfWork #AIlayoffs #BusinessStrategy #ProjectOlivia
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-mass-layoff-reckoning" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>How are you protecting your team while scaling AI automation?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-mass-layoff-reckoning')} className="like-button-ai-mass-layoff-reckoning">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-mass-layoff-reckoning')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-mass-layoff-reckoning')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-mass-layoff-reckoning')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-mass-layoff-reckoning"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-mass-layoff-reckoning');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'is-ai-in-a-bubble-part-2') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - Is AI In A Bubble Part 2 */}
                <BlogFullPost id="is-ai-in-a-bubble-part-2">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business strategy</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>is ai in a bubble? part 2 - time to prepare is now</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 8, 2025 at 10:00 am PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      welcome to part two of our AI bubble analysis. the first episode surfaced the headline metrics; this follow-up shows the undercurrents investors and operators are whispering about—valuation compression, enterprise retainer risk, and capital rotation toward durable agent outcomes.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        src={IS_AI_BUBBLE_PART2_VIDEO_URL}
                        title="Is AI In A Bubble? Part 2 - Time To Prepare Is NOW"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      ></iframe>
                    </VideoEmbedContainer>

                    <BlogPostSubheading>what changed since part one</BlogPostSubheading>
                    <BlogPostList>
                      <li>valuation spreads tightened by 18–25% for late-stage AI rounds while seed rounds hold price—but only for teams with live usage data.</li>
                      <li>enterprise buyers are freezing net-new pilots until vendors prove measurable cost takeout within one quarter.</li>
                      <li>capital is rotating from hype categories into “agent-in-production” platforms with verifiable security, compliance, and governance.</li>
                    </BlogPostList>

                    <BlogPostSubheading>three lenses to read the market</BlogPostSubheading>
                    <BlogPostText>
                      treat the cycle like a stress test across fundamentals, liquidity, and defensibility. when you watch those lenses weekly, you can act before the herd reacts.
                    </BlogPostText>
                    <BlogPostList>
                      <li><strong>fundamentals:</strong> revenue per GPU hour and gross margin delta after automation. if those trend up, you own leverage.</li>
                      <li><strong>liquidity:</strong> cash conversion cycle + burn. the market is rewarding teams that can self-fund growth within 18 months.</li>
                      <li><strong>defensibility:</strong> proprietary data rights, domain-specific tooling, and customer switching friction.</li>
                    </BlogPostList>

                    <Callout>
                      <CalloutTitle>the window is still open—narrowing fast</CalloutTitle>
                      <CalloutText>
                        the teams who convert hype into durable revenue before q2 2026 will inherit the budgets that wash out of speculative vendors. you do that by proving reliability, compliance, and line-of-business impact now.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>action plan for founders & operators</BlogPostSubheading>
                    <BlogPostList>
                      <li>run bubble drills: model a 30% pricing drop from your model provider and ensure you still clear gross margin targets.</li>
                      <li>ship transparency dashboards that let customers monitor agent accuracy, cost and approvals in real time.</li>
                      <li>lock in multi-year contracts by bundling guarantees—response time, security posture, audit readiness.</li>
                      <li>stand up an internal market desk: track daily sentiment across VCs, public indicators, and customer budget memos.</li>
                    </BlogPostList>

                    <BlogPostSubheading>what happens post-correction</BlogPostSubheading>
                    <BlogPostText>
                      when the market clears, winners will be the platforms already embedded in workflows with provable ROI. position your agents as the fabric for mission-critical work, not experiments. readiness today writes the growth story tomorrow.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AIbubble #MarketAnalysis #AgentStrategy #ProjectOlivia
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-is-ai-in-a-bubble-part-2" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What signals are you watching to judge the AI market cycle?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('is-ai-in-a-bubble-part-2')} className="like-button-is-ai-in-a-bubble-part-2">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('is-ai-in-a-bubble-part-2')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('is-ai-in-a-bubble-part-2')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('is-ai-in-a-bubble-part-2')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/is-ai-in-a-bubble-part-2"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('is-ai-in-a-bubble-part-2');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'is-ai-in-a-bubble') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - Is AI In A Bubble */}
                <BlogFullPost id="is-ai-in-a-bubble">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business strategy</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>is ai in a bubble? (time to prepare is now)</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 8, 2025 at 8:00 am PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      founders keep asking if AI is in a bubble. valuations are overheated, funding memos sound identical, and yet the usage curves refuse to slow down. bubbles are brutal only for teams who mistake hype for adoption. the builders who read the signals correctly will treat this moment as a once-in-a-decade reset—not a cliff.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        src={IS_AI_BUBBLE_VIDEO_URL}
                        title="Is AI In A Bubble? (Time To Prepare Is NOW)"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      ></iframe>
                    </VideoEmbedContainer>

                    <BlogPostSubheading>hype vs. fundamentals</BlogPostSubheading>
                    <BlogPostText>
                      every cycle leaves breadcrumbs. the public discourse shifts from product to price, investors tighten diligence on efficiency, and customers ask for predictable outcomes—not demos. do not ignore equities-lifetime value ratios, recurring usage telemetry, and the quality of revenue that your agents produce. these ratios decide whether the market resets around you or because of you.
                    </BlogPostText>

                    <BlogPostList>
                      <li>track capital efficiency: revenue per dollar of compute spend is now a board-level KPI.</li>
                      <li>watch unit demand: daily active agent workflows and completion rates show real pull, not headlines.</li>
                      <li>audit dependency risk: how exposed are you to model pricing changes, GPU allocation, or a single vendor roadmap?</li>
                    </BlogPostList>

                    <Callout>
                      <CalloutTitle>cycles redistribute power</CalloutTitle>
                      <CalloutText>
                        when markets cool, budgets consolidate around the agents that already deliver measurable outcomes. this is not about surviving a crash—it is about owning the standard your buyers use to evaluate everyone else.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>how to prepare now</BlogPostSubheading>
                    <BlogPostText>
                      reposition your narrative from speculation to operating leverage. bundle proof points that show margin expansion, compliance wins, and workflows your competitors cannot run. pair that with disciplined go-to-market sequencing so you enter every 2026 budget conversation with hard numbers.
                    </BlogPostText>

                    <BlogPostList>
                      <li>lock in strategic customers with multi-year pricing anchored to delivered value, not usage vagueness.</li>
                      <li>diversify model + infrastructure dependencies so GPU rotations or API repricing do not crush margins overnight.</li>
                      <li>publish transparent telemetry dashboards that let buyers verify uptime, accuracy, and cost savings in real time.</li>
                    </BlogPostList>

                    <BlogPostSubheading>what happens after the correction</BlogPostSubheading>
                    <BlogPostText>
                      if a correction hits, capital shifts from quick experiments to platforms that can shoulder entire business lines. the founders who invested in reliability, governance, and market fit will scale faster once the noise clears. prepare for that acceleration now.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AIbubble #MarketAnalysis #BusinessStrategy #ProjectOlivia
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-is-ai-in-a-bubble" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>How are you stress-testing your AI agent roadmap for the next market cycle?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('is-ai-in-a-bubble')} className="like-button-is-ai-in-a-bubble">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('is-ai-in-a-bubble')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('is-ai-in-a-bubble')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('is-ai-in-a-bubble')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/is-ai-in-a-bubble"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('is-ai-in-a-bubble');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'ai-agents-will-replace-you-unless-you-do-this') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - AI Agents Will Replace You Unless You Do This */}
                <BlogFullPost id="ai-agents-will-replace-you-unless-you-do-this">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business strategy</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>ai agents will replace you unless you do this</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 6, 2025 at 9:30 am PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      everyone is shouting that AI agents will automate every role. the truth? only the builders who deliver agents that can be trusted, who learn their operators, and who have full access to the systems that matter will win. everyone else ships a glorified chatbot that executives shut off after week one.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        src={AI_AGENTS_REPLACE_YOU_VIDEO_URL}
                        title="AI Agents will replace you unless you do this"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      ></iframe>
                    </VideoEmbedContainer>

                    <BlogPostSubheading>the three gaps killing 99% of agents</BlogPostSubheading>
                    <BlogPostList>
                      <li>they cannot be trusted to run in the background without breaking policy, budget, or customer trust.</li>
                      <li>they have no lasting memory of the humans, businesses, or institutional guardrails they serve.</li>
                      <li>they are fenced off from the computer, SaaS, and data-layer tools that actually move work forward.</li>
                    </BlogPostList>

                    <BlogPostText>
                      this is why buyers churn. they feel the hype, deploy the agent, then discover it needs babysitting, forgets context hourly, and cannot touch the billing portal or erp. when that happens, your champion hits pause and hunts for a competitor that can carry weight.
                    </BlogPostText>

                    <Callout>
                      <CalloutTitle>this is a historic window</CalloutTitle>
                      <CalloutText>
                        because most founders still ship agents without persistence, governance, or tool depth, we have the rarest advantage—an uncrowded field where a reliable agent instantly becomes the default operating system for an industry.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>designing agents users can trust</BlogPostSubheading>
                    <BlogPostText>
                      reliability is not just uptime. it is the combination of regression testing, human-in-the-loop circuit breakers, and telemetry that shows every action the agent took. give operators replayable timelines, fallbacks, and the ability to stop a workflow mid-flight. when your agent can run overnight and wake up with receipts, adoption sticks.
                    </BlogPostText>

                    <BlogPostSubheading>teach your agent to memorize the organization</BlogPostSubheading>
                    <BlogPostText>
                      your differentiated moat is the agent&apos;s understanding of the user. build policy graphs, preference stores, and contextual memory loops so the agent knows when legal needs to sign off, how procurement codes invoices, and what tone the ceo prefers in outbound emails. the AI winners will sound like the customer because they know the customer better than anyone else.
                    </BlogPostText>

                    <BlogPostSubheading>give your agents full-stack tool access</BlogPostSubheading>
                    <BlogPostText>
                      agents stuck behind an api never created leverage. wire them into browsers, terminal automation, custom enterprise apps, and secure rpa-style actions. the minute your agent can open the same systems your team uses—salesforce, netsuite, sharepoint, custom react panels—it graduates from demo to mission-critical.
                    </BlogPostText>

                    <BlogPostSubheading>what to operationalize this week</BlogPostSubheading>
                    <BlogPostList>
                      <li>ship reliability dashboards that track completion rate, intervention rate, and exception patterns per workflow.</li>
                      <li>implement a memory layer that captures user preferences, policy triggers, and organizational structures with governance controls.</li>
                      <li>integrate secure computer-use capabilities so agents can touch every cloud system the team touches—with audit trails.</li>
                      <li>package these wins into ROI stories that show hours returned, errors prevented, and revenue accelerated.</li>
                    </BlogPostList>

                    <BlogPostText>
                      the market is wide open. most teams will waste 2026 proving why agents do not work. the few who solve reliability, contextual learning, and tool access will own the category—and nobody will catch them.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AgentReliability #AgentEngineering #BusinessStrategy #ProjectOlivia
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-agents-will-replace-you-unless-you-do-this" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Where are you investing to make your AI agents reliable, personalized, and fully tooled?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agents-will-replace-you-unless-you-do-this')} className="like-button-ai-agents-will-replace-you-unless-you-do-this">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-agents-will-replace-you-unless-you-do-this')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agents-will-replace-you-unless-you-do-this')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agents-will-replace-you-unless-you-do-this')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-agents-will-replace-you-unless-you-do-this"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agents-will-replace-you-unless-you-do-this');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'ai-infrastructure-bottleneck') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - AI Infrastructure Bottleneck */}
                <BlogFullPost id="ai-infrastructure-bottleneck">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent infrastructure</BlogPostCategoryTag>
                      <BlogPostCategoryTag>energy infrastructure</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the AI infrastructure bottleneck keeping me up at night</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 6, 2025 at 7:00 am PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={AI_INFRASTRUCTURE_BOTTLENECK_IMAGE}
                        alt="High-voltage electrical transmission towers and power grid infrastructure representing energy distribution bottlenecks"
                      />
                      <BlogPostCaption>
                        the energy demand for AI agents is racing ahead of the grid. the teams who plan for power, efficiency, and availability now will own the decade.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      here&apos;s what keeps me up at night: the world wants always-on AI agents, but our infrastructure roadmap is still stuck in yesterday&apos;s assumptions. we architect delightful agent experiences and forget the physics that make them possible.
                    </BlogPostText>

                    <BlogPostSubheading>the math that wakes me up</BlogPostSubheading>
                    <BlogPostText>
                      the extropic team just published a model that aligns with what I&apos;ve been sketching on whiteboards. production AI agents running on today&apos;s GPU stacks already consume roughly 3 gigawatts worldwide. universal assistants jump that baseline to 100 gigawatts. stream video outputs at 1 fps and you add another order of magnitude. upgrade to reasoning-grade models and you do it again.
                    </BlogPostText>

                    <BlogPostList>
                      <li>a 30x demand surge the moment everyone has an AI co-pilot.</li>
                      <li>another 10x once agents render even low-frame-rate video experiences.</li>
                      <li>a further 10x when we shift from autocomplete to high-parameter reasoning models.</li>
                    </BlogPostList>

                    <BlogPostText>
                      now we are staring at 5+ terawatts of new infrastructure. covering nevada in solar would barely cover it. sam altman&apos;s push for a one-gigawatt data center every week requires 3-5x our current global energy production rate. the numbers do not lie—the bottleneck is real.
                    </BlogPostText>

                    <BlogPostSubheading>why everyone is building in silence</BlogPostSubheading>
                    <BlogPostText>
                      boardrooms understand the cliff. they just do not talk about it publicly because it moves stock prices and invites scrutiny. meanwhile they are racing to secure generation, transmission rights, and exclusive compute contracts. the agents keep shipping while the energy story stays off the slides.
                    </BlogPostText>

                    <Callout>
                      <CalloutTitle>physics-native compute is the unlock</CalloutTitle>
                      <CalloutText>
                        extropic&apos;s thermodynamic sampling units promise 10,000x energy efficiency by designing compute around physics, not forcing physics to imitate GPUs. if that ships and scales, every assumption about agent cost curves changes overnight.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>physics-native compute changes the equation</BlogPostSubheading>
                    <BlogPostText>
                      GPUs are phenomenal at parallel math, but they are blunt instruments for the kind of probabilistic sampling modern agents require. thermodynamic compute leans on the natural tendency of physical systems to explore state space efficiently. it is not magic; it is the first time we have aligned hardware with the workloads agents actually run.
                    </BlogPostText>
                    <BlogPostText>
                      pair that with smart power procurement—onsite renewables, long-duration storage, firmed capacity contracts—and the 5-terawatt shock softens. the teams who orchestrate both sides are the ones who capture market share when demand spikes.
                    </BlogPostText>

                    <BlogPostSubheading>what builders should operationalize right now</BlogPostSubheading>
                    <BlogPostList>
                      <li>audit your agent roadmap against real power budgets—know how many kilowatt-hours every user workflow consumes.</li>
                      <li>partner with data center operators and utilities early; negotiate green baseload and co-location near generation assets.</li>
                      <li>prototype with physics-first compute vendors so you are ready the moment their hardware clears pilots.</li>
                      <li>instrument carbon, latency, and cost telemetry so procurement, compliance, and customers see the impact in one dashboard.</li>
                    </BlogPostList>

                    <BlogPostText>
                      we need breakthrough efficiency tech and new power capacity. only one of those can be built by a dozen engineers in a garage outside boston. that is where I am betting—and where the next decade of AI infrastructure will be won.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AIinfrastructure #EnergyInfrastructure #Extropic #ProjectOlivia
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-infrastructure-bottleneck" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>How are you preparing your AI infrastructure stack for 5+ terawatts of demand?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-infrastructure-bottleneck')} className="like-button-ai-infrastructure-bottleneck">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-infrastructure-bottleneck')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-infrastructure-bottleneck')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-infrastructure-bottleneck')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-infrastructure-bottleneck"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-infrastructure-bottleneck');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'ai-agent-security-crisis-nobody-talking-about') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - AI Agent Security Crisis */}
                <BlogFullPost id="ai-agent-security-crisis-nobody-talking-about">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>cybersecurity</BlogPostCategoryTag>
                      <BlogPostCategoryTag>identity security</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the #1 security crisis nobody's talking about</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 5, 2025 at 7:30 am PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={AI_AGENT_SECURITY_IMAGE}
                        alt="Security operations team monitoring AI agent activity in a glass-walled cyber defense center"
                      />
                      <BlogPostCaption>
                        identity-first security for AI agents is becoming the line between innovation and the next major breach headline.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      82% of enterprises now run AI agents in production. Fewer than 10% have the guardrails to control them. The AI agent revolution is creating its biggest own goal: speed without security.
                    </BlogPostText>

                    <BlogPostSubheading>why this is happening now</BlogPostSubheading>
                    <BlogPostText>
                      The infrastructure to build agents is mature—OpenAI, Anthropic, Azure, and the open-source ecosystem solved that. The infrastructure to govern agents is still an experiment. Most teams grant broad API keys, skip privilege boundaries, and hope audit logs tell the story later. They rarely do.
                    </BlogPostText>

                    <BlogPostSubheading>what the data says</BlogPostSubheading>
                    <BlogPostText>
                      Fresh CISO research shows 2 out of 3 security leaders rank agentic AI as their #1 emerging cyber risk. Nearly 40% of financial institutions have agents in production with little to no governance. When your agent can reach databases, CRMs, and customer records without identity-aware controls, "innovation" becomes a liability.
                    </BlogPostText>

                    <BlogPostSubheading>identity-first agent security is the moat</BlogPostSubheading>
                    <BlogPostText>
                      Identity security leaders like CyberArk, Akeyless, and Oasis just launched agent governance frameworks. The winners will adopt them on day one—mapping agents to identities, enforcing least privilege, and capturing every action. The losers will spend 18 months untangling credential sprawl, breached workflows, and compliance nightmares.
                    </BlogPostText>

                    <Callout>
                      <CalloutTitle>secure agents like you secure people</CalloutTitle>
                      <CalloutText>
                        • map every agent to an identity and rotate credentials automatically.<br />
                        • scope access to the task, not the entire system.<br />
                        • log every workflow step—actions, tools, datasets, outcomes.<br />
                        • add human-in-the-loop review for high-risk operations.
                      </CalloutText>
                    </Callout>

                    <BlogPostSubheading>the adoption math is unforgiving</BlogPostSubheading>
                    <BlogPostText>
                      Agents will outnumber human employees at many enterprises before year-end. That shift is not hype—it is compounding automation. If security teams don’t embed identity governance now, every new agent compounds risk and regulatory exposure.
                    </BlogPostText>

                    <BlogPostSubheading>make security the growth lever</BlogPostSubheading>
                    <BlogPostText>
                      Builders who combine agent velocity with verifiable security unlock the largest contracts. Regulated industries are desperate for teams who can ship automation without sacrificing posture. That is the revenue unlock hiding inside this "crisis".
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AgentSecurity #IdentityFirst #Cybersecurity #AIGovernance
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-agent-security-crisis-nobody-talking-about" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What’s your agent security strategy? Drop it below.</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agent-security-crisis-nobody-talking-about')} className="like-button-ai-agent-security-crisis-nobody-talking-about">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-agent-security-crisis-nobody-talking-about')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agent-security-crisis-nobody-talking-about')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agent-security-crisis-nobody-talking-about')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-agent-security-crisis-nobody-talking-about"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agent-security-crisis-nobody-talking-about');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'ai-agent-opportunity-nobody-talking-about-yet') {
                    return (
                      <React.Fragment key={post.id}>
                {/* Featured blog post - AI Agent Opportunity */}
                <BlogFullPost id="ai-agent-opportunity-nobody-talking-about-yet">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business opportunity</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the AI agent opportunity nobody is talking about yet</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 4, 2025 at 8:30 am PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={AI_AGENT_OPPORTUNITY_IMAGE}
                        alt="Executive team reviewing AI agent strategy roadmap on a glass wall"
                      />
                      <BlogPostCaption>
                        the next wave of AI agents will be built by teams who remove the hard operational problems—handoffs, guardrails, and human escalation—before anyone else.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      AI agents are failing in production today. They hallucinate workflows, mishandle guardrails, and stall when an edge case hits. Every one of those failure modes is a greenfield opportunity for builders who can ship reliable behavior into real organizations.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        title="The AI Agent Opportunity Nobody Is Talking About Yet"
                        src="https://www.youtube.com/embed/ACkgFlpHD8k?si=qqaQLI-pyk8cT1qX"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </VideoEmbedContainer>

                    <BlogPostSubheading>the unsolved friction inside today&apos;s agents</BlogPostSubheading>
                    <BlogPostText>
                      Most teams still wire agents like demos: a single prompt, a brittle decision tree, and no persistent memory. The result is a pilot that breaks under real volume. If you can engineer safe recoveries, audit trails, and task-switching that respects human inputs, you own a moat that incumbents can&apos;t buy.
                    </BlogPostText>

                    <BlogPostSubheading>where builders win right now</BlogPostSubheading>
                    <BlogPostText>
                      The fastest path to shipping is building &quot;agent companions&quot; that monitor operational steps and take over where repetition exists. Focus on finite, measurable workloads: inbound qualification, renewal prep, post-sale onboarding, or compliance intake. Automate the grind, leave the judgment to people, and collect the data that improves next week&apos;s release.
                    </BlogPostText>

                    <BlogPostSubheading>turn breakdowns into launch blueprints</BlogPostSubheading>
                    <BlogPostText>
                      Document the root cause of every agent failure you find—missing context, bad routing, or incorrect tool sequencing. Each one becomes a packaged solution you can deliver repeatedly. That is the difference between building a tool and building a defensible product line.
                    </BlogPostText>

                    <BlogPostSubheading>watch the breakdown</BlogPostSubheading>
                    <BlogPostText>
                      In this breakdown, I map out the biggest production gaps slowing agents today and the simple workflow fixes that unlock them. If you&apos;re ready to build an AI agent that replaces meetings—not headcount—start here.
                    </BlogPostText>

                    <BlogPostText>
                      #AIagents #AgentEngineering #OpportunityGap #BuilderPlaybook #AI2025
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-agent-opportunity-nobody-talking-about-yet" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Where do you see the biggest AI agent gap worth fixing this week?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agent-opportunity-nobody-talking-about-yet')} className="like-button-ai-agent-opportunity-nobody-talking-about-yet">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-agent-opportunity-nobody-talking-about-yet')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agent-opportunity-nobody-talking-about-yet')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agent-opportunity-nobody-talking-about-yet')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-agent-opportunity-nobody-talking-about-yet"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agent-opportunity-nobody-talking-about-yet');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  if (post.id === 'agentic-ai-window-closing-fast') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - Agentic AI Window */}
                <BlogFullPost id="agentic-ai-window-closing-fast">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                      <BlogPostCategoryTag>future of work</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the agentic AI window just opened. it's closing faster than you think.</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 2, 2025 at 9:00 am PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      For 18 months, agentic AI lived in a gap between the hype cycle and real adoption. That gap just closed.
                    </BlogPostText>

                    <VideoEmbedContainer style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        title="The Agentic AI Window Just Opened"
                        src="https://www.youtube.com/embed/LHtaVMOQ_Qo?si=hSqatvbnJrdVDY9j"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </VideoEmbedContainer>

                    <BlogPostSubheading>the adoption curve just flipped</BlogPostSubheading>
                    <BlogPostText>
                      In Q1 2025, VC funding for agentic AI jumped 265%. Gartner crowned it the number one strategic trend of the year. Enterprise adoption surged from 11% to 68% in six months. Infrastructure is ready, business cases are obvious, and skepticism is evaporating.
                    </BlogPostText>

                    <BlogPostSubheading>why this moment matters</BlogPostSubheading>
                    <BlogPostText>
                      Autonomous agents are not coming to replace jobs—they are redefining how work happens across sales, support, supply chain, code generation, and every operational lane. The teams capturing outsized value will not be the ones who bought a tool; they will be the ones who built, shipped, and specialized agents for the exact problems they own.
                    </BlogPostText>

                    <BlogPostSubheading>behavior engineering is the moat</BlogPostSubheading>
                    <BlogPostText>
                      You no longer need a Stanford PhD to engineer powerful agents. Platforms are accessible, behavior engineering frameworks exist, and your procedures, policies, and preferences form the long-tail knowledge that competitors cannot copy. That is the moat.
                    </BlogPostText>

                    <BlogPostSubheading>build it. ship it. specialize it.</BlogPostSubheading>
                    <BlogPostText>
                      The companies that win will wire their institutional knowledge directly into agents and deploy them into production workflows. That is the window that just opened—and it's the same window that will close as platform consolidation and regulatory gatekeeping arrive.
                    </BlogPostText>

                    <BlogPostSubheading>the window won't stay open</BlogPostSubheading>
                    <BlogPostText>
                      Platform consolidation, regulatory guardrails, and market concentration always follow rapid adoption. Right now the field is wide open. The builders who move first set the standards everyone else follows.
                    </BlogPostText>

                    <BlogPostText>
                      #AIAgents #AgenticAI #BuildersMindset #TechOpportunity #AI2025
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-agentic-ai-window-closing-fast" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What are you building before the window closes?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('agentic-ai-window-closing-fast')} className="like-button-agentic-ai-window-closing-fast">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('agentic-ai-window-closing-fast')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('agentic-ai-window-closing-fast')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('agentic-ai-window-closing-fast')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/agentic-ai-window-closing-fast"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('agentic-ai-window-closing-fast');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }

                  if (post.id === 'openai-trillion-dollar-ipo-starting-gun') {
                    return (
                      <React.Fragment key={post.id}>
                <BlogFullPost id="openai-trillion-dollar-ipo-starting-gun">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                      <BlogPostCategoryTag>founder insights</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>OpenAI's $1 trillion ipo just fired the starting gun</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      November 1, 2025 at 8:00 am PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={OPENAI_IPO_IMAGE}
                        alt="Stock market trading floor representing OpenAI's upcoming $1 trillion IPO"
                      />
                      <BlogPostCaption>
                        openai's $1 trillion ipo signals the AI agent revolution has entered its infrastructure phase—the largest technology business opportunity the world has ever seen.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostSubheading>The Signal Everyone's Been Waiting For</BlogPostSubheading>
                    
                    <BlogPostText>
                      OpenAI is preparing for a $1 trillion IPO—potentially the largest in history. This isn't just another tech company going public. This is the signal that the AI agent revolution has entered its infrastructure phase.
                    </BlogPostText>

                    <BlogPostText>
                      While OpenAI builds the foundation, the real opportunity is at the application layer. The people building custom AI agents for specific industries and use cases are positioned to capture disproportionate value in the next 18-24 months.
                    </BlogPostText>

                    <BlogPostSubheading>The Numbers Are Staggering</BlogPostSubheading>
                    
                    <BlogPostText>
                      The AI agents market is projected to explode from $5.1 billion in 2024 to $47.1 billion by 2030—a 44.8% CAGR. PwC's May 2025 survey shows 88% of executives plan to increase AI budgets specifically for agentic AI. Early adopters are already reporting 66% productivity gains.
                    </BlogPostText>

                    <BlogPostText>
                      This is the largest technology business opportunity the world has ever seen, and the window is wide open right now.
                    </BlogPostText>

                    <BlogPostSubheading>Why Timing Matters</BlogPostSubheading>
                    
                    <BlogPostText>
                      The market is mature enough to build on, but immature enough that there's no entrenched competition. Foundation models are stable. Tools are accessible. Infrastructure is in place. But the massive work of teaching AI agents the unique preferences, policies, and specialized knowledge for every industry? That hasn't been done yet.
                    </BlogPostText>

                    <BlogPostText>
                      This is the opportunity. Engineering behavior into agents that handle your specific workflows. Building custom agents that solve real problems. The work is behavior engineering, not traditional software development. And anyone can start today.
                    </BlogPostText>

                    <BlogPostSubheading>You Don't Need Permission to Start</BlogPostSubheading>
                    
                    <BlogPostText>
                      You don't need a CS degree. You don't need venture capital. You don't need a team. You need domain expertise, curiosity, and the willingness to start building.
                    </BlogPostText>

                    <BlogPostText>
                      OpenAI's IPO is the starting gun. Market maturation takes time, giving early builders space to iterate. But the window closes fast once capital floods in.
                    </BlogPostText>

                    <BlogPostSubheading>The Asymmetric Opportunity</BlogPostSubheading>
                    
                    <BlogPostText>
                      Everyone should be building AI agents. Not because it's trendy. Because it's the most asymmetric opportunity in modern business history.
                    </BlogPostText>

                    <BlogPostText>
                      The question isn't whether AI agents will reshape every industry. They already are. The question is: will you be building, or watching others build?
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-openai-trillion-dollar-ipo-starting-gun" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What's your take on OpenAI's IPO and the AI agent opportunity?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('openai-trillion-dollar-ipo-starting-gun')} className="like-button-openai-trillion-dollar-ipo-starting-gun">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.0 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('openai-trillion-dollar-ipo-starting-gun')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('openai-trillion-dollar-ipo-starting-gun')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('openai-trillion-dollar-ipo-starting-gun')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'ai-agent-app-store-phase') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - AI Agent App Store Phase */}
                <BlogFullPost id="ai-agent-app-store-phase">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                      <BlogPostCategoryTag>founder insights</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the AI agent revolution just entered the "app store" phase</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 31, 2025 at 9:00 am PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={AI_AGENT_APP_STORE_IMAGE}
                        alt="ChatGPT AI interface on smartphone showing conversational AI agent interaction"
                      />
                      <BlogPostCaption>
                        the chatgpt interface represents the new distribution channel for AI agents—800 million weekly users now accessible through openai's apps sdk.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostSubheading>The Distribution Problem Just Got Solved</BlogPostSubheading>
                    
                    <BlogPostText>
                      Two weeks ago, if you wanted to build an AI agent, you faced a distribution problem: How do you get users to actually use it?
                    </BlogPostText>

                    <BlogPostText>
                      That problem just got solved.
                    </BlogPostText>

                    <BlogPostText>
                      OpenAI launched the ChatGPT Apps SDK, and it's bigger than most people realize. Not because it's technically clever—it's because it hands developers an instant distribution channel to 800 million weekly active users.
                    </BlogPostText>

                    <BlogPostSubheading>Think About What Just Happened</BlogPostSubheading>
                    
                    <BlogPostText>
                      <strong>For AI Agent Builders:</strong> You can now build an autonomous agent (customer service bot, code generator, sales researcher—whatever) and deploy it to ChatGPT's ecosystem. No landing page. No marketing budget. ChatGPT's algorithm surfaces your app to millions of relevant users.
                    </BlogPostText>

                    <BlogPostText>
                      <strong>For Enterprise:</strong> You can operationalize AI agents in weeks, not months. Multi-agent teams coordinating customer service, finance, operations—all running inside ChatGPT with rich UIs and real-time data.
                    </BlogPostText>

                    <BlogPostText>
                      <strong>For the Market:</strong> OpenAI's $500B valuation signals investors believe this is the next computing platform. Not hype. Capital.
                    </BlogPostText>

                    <BlogPostSubheading>Meanwhile, The Intelligence Layer Keeps Getting Better</BlogPostSubheading>
                    
                    <BlogPostText>
                      Claude Sonnet 4.5 just hit 77.2% on SWE-bench Verified (beats GPT-5), and Microsoft released open-source Agent Framework for orchestrating multi-agent systems at enterprise scale.
                    </BlogPostText>

                    <BlogPostText>
                      Translation: The infrastructure for autonomous AI agents is production-ready. Distribution is solved. Intelligence is proven.
                    </BlogPostText>

                    <BlogPostSubheading>This Is The Inflection Point</BlogPostSubheading>
                    
                    <BlogPostText>
                      Not when agents will be powerful—when smart builders realize they already are.
                    </BlogPostText>

                    <BlogPostText>
                      If you're founder or leader evaluating AI for 2026, today is the day you stop asking "should we use AI agents?" and start asking "which problem do we solve with them first?"
                    </BlogPostText>

                    <BlogPostText>
                      The window for first-mover advantage isn't years—it's months.
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-agent-app-store-phase" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What's your take on the AI agent "app store" phase?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agent-app-store-phase')} className="like-button-ai-agent-app-store-phase">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-agent-app-store-phase')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agent-app-store-phase')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agent-app-store-phase')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-agent-app-store-phase"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agent-app-store-phase');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'secret-hidden-ai-revolution') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - The Secret Hidden in Plain Sight */}
                <BlogFullPost id="secret-hidden-ai-revolution">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                      <BlogPostCategoryTag>business opportunity</BlogPostCategoryTag>
                      <BlogPostCategoryTag>founder insights</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the secret about ai agents hidden in plain sight</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 30, 2025 at 12:00 pm PST
                    </BlogPostHeaderDate>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      the AI agent revolution is the largest business cycle opportunity humankind has ever seen and we are within weeks of it truly getting started
                    </BlogPostText>

                    <BlogPostText>
                      (what we should all do next)
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        title="The Secret Hidden in Plain Sight"
                        src="https://www.youtube.com/embed/77Ke0PZjnAE?si=e3V8wnW5lkVTU3t3"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </VideoEmbedContainer>

                    <BlogPostSubheading>About Miles Maggio</BlogPostSubheading>
                    <BlogPostText>
                      Miles Maggio is a long-time entrepreneur, startup founder, and featured speaker and teacher on AI agent engineering. He also founded prest0—the easiest way to build your AI agent with zero code.
                    </BlogPostText>

                    <BlogPostSubheading>The Largest Opportunity in History</BlogPostSubheading>
                    <BlogPostText>
                      The AI agent revolution represents a business cycle opportunity unprecedented in human history. Unlike previous technological shifts, this transformation is happening at an accelerated pace, with production-ready solutions emerging across every industry simultaneously.
                    </BlogPostText>

                    <BlogPostSubheading>We're Just Getting Started</BlogPostSubheading>
                    <BlogPostText>
                      Despite all the progress we've seen, we're still in the earliest days of this revolution. The infrastructure layer is being built right now. The workflow automation layer is emerging. And the value creation opportunities are stacking faster than most teams can capitalize on them.
                    </BlogPostText>

                    <BlogPostSubheading>What You Should Do Next</BlogPostSubheading>
                    <BlogPostText>
                      For builders, entrepreneurs, and business leaders: the time to act is now. Understand the infrastructure being built. Identify where agents can transform your workflows. Start shipping solutions that integrate into real operations. The window is open, but it won't stay open forever.
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-secret-hidden-ai-revolution" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>What's your take on the AI agent revolution?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('secret-hidden-ai-revolution')} className="like-button-secret-hidden-ai-revolution">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('secret-hidden-ai-revolution')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('secret-hidden-ai-revolution')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('secret-hidden-ai-revolution')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/secret-hidden-ai-revolution"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('secret-hidden-ai-revolution');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'claude-skills-enterprise-inflection') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - Claude Skills Enterprise Inflection */}
                <BlogFullPost id="claude-skills-enterprise-inflection">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>enterprise AI</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>the enterprise AI agent inflection point nobody's talking about</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 29, 2025 at 4:38 pm PST
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={CLAUDE_SKILLS_IMAGE}
                        alt="Modern enterprise workspace with AI infrastructure visualization"
                      />
                      <BlogPostCaption>
                        The enterprise AI agent market shifted from theory to production this week as Claude Skills solved the customization constraint that held back adoption.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostSubheading>The Inflection Point Nobody's Talking About</BlogPostSubheading>
                    
                    <BlogPostText>
                      For 18 months, AI agents existed as theory. Cool demos. Expensive consultants. Endless prompting.
                    </BlogPostText>

                    <BlogPostText>
                      Then something shifted last week.
                    </BlogPostText>

                    <BlogPostText>
                      Anthropic released Claude Skills. Enterprises actually deployed them. Today we're seeing:
                    </BlogPostText>

                    <BlogPostText>
                      Accenture investing in enterprise agent infrastructure (Lyzr)
                    </BlogPostText>

                    <BlogPostText>
                      Palo Alto Networks launching Cortex AgentiX for security teams
                    </BlogPostText>

                    <BlogPostText>
                      Security vendors stacking agentic solutions simultaneously
                    </BlogPostText>

                    <BlogPostText>
                      65% of businesses actively adopting agentic AI right now
                    </BlogPostText>

                    <BlogPostText>
                      Here's what changed: the constraint was never capability. It was customization.
                    </BlogPostText>

                    <BlogPostText>
                      Organizations can't deploy generic agents. They need agents that understand their workflows, compliance requirements, proprietary data structures. That meant hiring consultants. Endless prompt engineering. Delays.
                    </BlogPostText>

                    <BlogPostSubheading>Claude Skills Changed Everything</BlogPostSubheading>
                    
                    <BlogPostText>
                      Claude Skills solved that. You package institutional knowledge into reusable procedures. Claude loads them contextually. No consulting. No hand-holding.
                    </BlogPostText>

                    <BlogPostText>
                      Result? Actual production deployments. Not pilots. Not experiments.
                    </BlogPostText>

                    <BlogPostSubheading>The Market Signal</BlogPostSubheading>
                    
                    <BlogPostText>
                      This isn't hype. This is infrastructure. When multiple security vendors launch agentic solutions simultaneously (Akeyless, Palo Alto, Astrix, Seemplicity—all this week), enterprises are saying: "We're going all-in on agents. Now we need to secure them."
                    </BlogPostText>

                    <BlogPostText>
                      That's enterprise adoption. That's real.
                    </BlogPostText>

                    <BlogPostSubheading>What Most Builders Are Missing</BlogPostSubheading>
                    
                    <BlogPostText>
                      Most are still building generic agents. Wrong move.
                    </BlogPostText>

                    <BlogPostText>
                      The winners package domain expertise into Skills, not prompts.
                    </BlogPostText>

                    <BlogPostText>
                      Compliance and customization were always the real moat.
                    </BlogPostText>

                    <BlogPostText>
                      The $8.8B AI consulting industry is about to contract. Skills made it obsolete.
                    </BlogPostText>

                    <BlogPostSubheading>For Entrepreneurs</BlogPostSubheading>
                    
                    <BlogPostText>
                      This is your moment to stop demo-ing and start building systems that actually integrate into existing operations.
                    </BlogPostText>

                    <BlogPostText>
                      The infrastructure layer (agentic frameworks, security, compliance) is where capital and acquisitions move first.
                    </BlogPostText>

                    <BlogPostText>
                      Then the workflow layer (actual business process automation).
                    </BlogPostText>

                    <BlogPostText>
                      Your competitive advantage: you understand your domain better than anyone. That institutional knowledge? That's what becomes a Skill. That's what compounds.
                    </BlogPostText>

                    <BlogPostText>
                      Not hyping agents. Just noting: this week, the game changed.
                    </BlogPostText>

                    <BlogPostText>
                      Build accordingly.
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-claude-skills-enterprise-inflection" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Share your thoughts on the enterprise AI agent inflection point.</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('claude-skills-enterprise-inflection')} className="like-button-claude-skills-enterprise-inflection">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('claude-skills-enterprise-inflection')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('claude-skills-enterprise-inflection')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('claude-skills-enterprise-inflection')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/claude-skills-enterprise-inflection"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('claude-skills-enterprise-inflection');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'mem0-memory-infrastructure') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - Mem0 Memory Infrastructure Funding */}
                <BlogFullPost id="mem0-memory-infrastructure">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent infrastructure</BlogPostCategoryTag>
                      <BlogPostCategoryTag>memory systems</BlogPostCategoryTag>
                      <BlogPostCategoryTag>funding news</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>why agent memory infrastructure unlocks your largest opportunity</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 29, 2025 at 7:00 am
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={MEM0_IMAGE}
                        alt="Business analytics dashboard showing exponential market growth"
                      />
                      <BlogPostCaption>
                        The agent memory infrastructure market is projected to hit $7.6B as persistent context becomes the competitive moat builders can't ignore.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      Let's be honest: your AI agents have amnesia.
                    </BlogPostText>

                    <BlogPostText>
                      You explain your process to ChatGPT. Five minutes later, it forgets. Your coding assistant rejects the same pattern you already told it to avoid. Your customer support bot re-asks questions it answered yesterday. This isn't a limitation of the model—it's a fundamental architecture problem in how we've built agent systems.
                    </BlogPostText>

                    <BlogPostText>
                      Welcome to the $7.6B market opportunity everyone's racing to solve.
                    </BlogPostText>

                    <BlogPostText>
                      Today, Mem0 (backed by Y Combinator, Peak XV, and CEOs from Datadog, Supabase, and PostHog) announced $24M in Series A funding to do one thing: make AI agents remember.
                    </BlogPostText>

                    <BlogPostSubheading>Why This Matters</BlogPostSubheading>
                    <BlogPostText>
                      Single agents without persistent memory are expensive. Every interaction starts from zero context. That's why RAG pipelines feel like patches instead of solutions. But when you solve memory properly—when an agent can extract, organize, and retrieve relevant context across interactions—the economics flip. Personalization becomes real. Long-term learning becomes possible. Latency drops because you're not re-explaining yourself every five minutes.
                    </BlogPostText>

                    <BlogPostText>
                      Mem0's approach is elegant: three lines of code to integrate. Their API grew from 35M calls in Q1 to 186M in Q3 2025. AWS chose them as the exclusive memory provider for its Agent SDK. CrewAI, Flowise, and Langflow built them in natively.
                    </BlogPostText>

                    <BlogPostSubheading>What This Signals</BlogPostSubheading>
                    <BlogPostText>
                      Memory infrastructure is becoming what databases became in the 1990s—a foundational layer, not a feature. Founders building agents without solving memory are building on sand. The real competitive moat isn't the agent itself; it's the quality of memory you persist.
                    </BlogPostText>

                    <BlogPostText>
                      If you're integrating AI agents into your product in 2025—whether for customer support, internal ops, or knowledge work—you need to think about memory first. Not as an afterthought. Not as a retrieval hack. As a core architectural choice.
                    </BlogPostText>

                    <BlogPostText>
                      The agentic future depends on it.
                    </BlogPostText>

                    <BlogPostText>
                      What do you think—is memory infrastructure going to be the unsung hero of the agent era?
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-mem0-memory-infrastructure" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Join the discussion: does memory win the agent arms race?</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('mem0-memory-infrastructure')} className="like-button-mem0-memory-infrastructure">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('mem0-memory-infrastructure')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('mem0-memory-infrastructure')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('mem0-memory-infrastructure')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/mem0-memory-infrastructure"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('mem0-memory-infrastructure');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'ai-agent-inflection-point-video') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - AI Agent Inflection Point Video */}
                <BlogFullPost id="ai-agent-inflection-point-video">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>video spotlight</BlogPostCategoryTag>
                      <BlogPostCategoryTag>founder insights</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agent engineering</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>The AI Agent Inflection Point (Video Spotlight)</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 28, 2025 at 7:00 pm
                    </BlogPostHeaderDate>

                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      The AI agent revolution crossed an inflection point today, and the shift is unfolding faster than most teams realize.
                    </BlogPostText>

                    <VideoEmbedContainer>
                      <iframe
                        title="The AI Agent Inflection Point"
                        src="https://www.youtube.com/embed/-Wey81bB3Hw?si=BF5F2RhlUbAc2DWN"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </VideoEmbedContainer>

                    <BlogPostSubheading>About Miles Maggio</BlogPostSubheading>
                    <BlogPostText>
                      Miles Maggio is a long-time entrepreneur, startup founder, and featured speaker and teacher on AI agent engineering. He also founded prest0—the easiest way to build your AI agent with zero code.
                    </BlogPostText>

                    <BlogPostSubheading>Why This Moment Matters</BlogPostSubheading>
                    <BlogPostText>
                      AI agents present the largest business opportunity in history, multiple orders of magnitude bigger than prior technology waves, and still early enough for builders to gain an edge.
                    </BlogPostText>

                    <BlogPostSubheading>prest0's Mission</BlogPostSubheading>
                    <BlogPostText>
                      prest0 exists to democratize AI agents for everyone. We empower individuals and small businesses to launch their own AI agent interns without writing code.
                    </BlogPostText>

                    <BlogPostSubheading>Your Invitation</BlogPostSubheading>
                    <BlogPostText>
                      Learn how to leverage the AI revolution so you, your team, and your company can become a positive force on the planet—and capture value from the largest wealth transfer in human history.
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-ai-agent-inflection-point-video" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Be the first to comment on this video breakdown.</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agent-inflection-point-video')} className="like-button-ai-agent-inflection-point-video">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('ai-agent-inflection-point-video')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agent-inflection-point-video')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agent-inflection-point-video')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/ai-agent-inflection-point-video"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agent-inflection-point-video');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'paypal-agentic-commerce-inflection') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - PayPal Agentic Commerce */}
                <BlogFullPost id="paypal-agentic-commerce-inflection">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>agentic commerce</BlogPostCategoryTag>
                      <BlogPostCategoryTag>payments</BlogPostCategoryTag>
                      <BlogPostCategoryTag>technology</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>PayPal's Agentic Commerce Inflection Point</BlogPostHeading>

                    <TopAuthorInfo>
                      <TopAuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <TopAuthorDetails>
                        <TopAuthorName>Miles Maggio</TopAuthorName>
                        <TopAuthorRole>top voice, feat. speaker, and advisor on AI agent engineering</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>

                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      October 28, 2025 at 5:00 am
                    </BlogPostHeaderDate>

                    <BlogPostImageContainer>
                      <BlogPostImg
                        src={PAYPAL_AGENTIC_IMAGE}
                        alt="Modern commerce workspace showing e-commerce platform interface"
                      />
                      <BlogPostCaption>
                        PayPal's agentic commerce infrastructure connects 400 million merchants to AI surfaces where conversations convert directly to revenue.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>

                  <BlogPostSection>
                    <BlogPostText>
                      We just hit an inflection point in AI that nobody is talking about yet.
                    </BlogPostText>

                    <BlogPostText>
                      Today, PayPal announced agentic commerce services. If you understand what this means, you are two years ahead of the curve.
                    </BlogPostText>

                    <BlogPostText>
                      This is the moment agents stop being productivity toys and become revenue infrastructure.
                    </BlogPostText>

                    <BlogPostSubheading>Here's What Just Happened</BlogPostSubheading>
                    <BlogPostList>
                      <li>Millions of merchants can now accept payments on AI surfaces like ChatGPT and Perplexity with zero additional engineering.</li>
                      <li>PayPal eliminated custom APIs and integration headaches by shipping a plug-and-play payments layer for agents.</li>
                      <li>Agent conversations can convert directly to revenue inside one flow instead of shuttling people back to traditional storefronts.</li>
                    </BlogPostList>

                    <BlogPostText>
                      Overnight, 400 million PayPal merchants gained an on-ramp to agentic commerce and 800 million ChatGPT users gained in-chat purchasing power.
                    </BlogPostText>

                    <BlogPostSubheading>The Shift</BlogPostSubheading>
                    <BlogPostText>
                      For twenty five years, e-commerce followed a linear playbook.
                    </BlogPostText>
                    <BlogPostList>
                      <li><strong>Old flow:</strong> Search → Browse → Cart → Checkout.</li>
                      <li><strong>New flow:</strong> Chat → Agent reasoning → Direct purchase.</li>
                    </BlogPostList>
                    <BlogPostText>
                      Ask ChatGPT what shoes you need for a 10k and the agent now reasons through your goals, surfaces products from thousands of merchants, and checks out in the same conversation.
                    </BlogPostText>

                    <BlogPostSubheading>What PayPal Solved</BlogPostSubheading>
                    <BlogPostText>
                      The breakthrough is not just technology, it is distribution and friction removal.
                    </BlogPostText>
                    <BlogPostList>
                      <li><strong>Old model:</strong> Build an agent, bolt on payments, deploy, and repeat the same custom work for every surface.</li>
                      <li><strong>New model:</strong> Merchant joins Store Sync once, PayPal syndicates the catalog everywhere agents operate, and distribution becomes infinite.</li>
                    </BlogPostList>

                    <BlogPostSubheading>What's Happening Right Now</BlogPostSubheading>
                    <BlogPostText>
                      While people debate whether agents are hype, infrastructure leaders are locking in the stack.
                    </BlogPostText>
                    <BlogPostList>
                      <li>Anthropic Claude Skills (Oct 16) delivers reusable organizational knowledge for agents.</li>
                      <li>Meta OpenEnv (Oct 27) open-sources complex agentic environments.</li>
                      <li>Salesforce Agentforce (Oct 12) already fields more than 50,000 inquiries per week via AI SDRs.</li>
                      <li>OpenAI AgentKit (Oct 5) gives non-ML teams a visual agent builder.</li>
                      <li>PayPal Agentic Commerce (Oct 28) provides the revenue infrastructure everyone else needed.</li>
                    </BlogPostList>

                    <BlogPostSubheading>Your Move</BlogPostSubheading>
                    <BlogPostList>
                      <li>If you are building agent platforms, payments integration is now mandatory.</li>
                      <li>If you operate in e-commerce, your product data has to be AI-discoverable immediately.</li>
                      <li>If you focus on vertical agents, wire revenue infrastructure into your roadmap this quarter.</li>
                    </BlogPostList>

                    <BlogPostSubheading>The Bottom Line</BlogPostSubheading>
                    <BlogPostText>
                      AI is shifting from tool to interface. PayPal's commerce layer, Anthropic's skills, Meta's standards, Salesforce's orchestration, and OpenAI's deployment toolkit are building one shared story: agentic infrastructure that billions will depend on.
                    </BlogPostText>

                    <BlogPostText>
                      What's your move? Building for agents, or waiting?
                    </BlogPostText>

                    <BlogPostText>
                      Miles Maggio | top voice, feat. speaker, and advisor on AI agent engineering
                    </BlogPostText>

                    <BlogPostText>
                      #AgenticAI #AIAgents #FutureOfCommerce #AI #TechLeadership #Startups #Innovation
                    </BlogPostText>

                    <Divider />

                    <AuthorInfo>
                      <AuthorAvatar src="/miles-maggio-profile.webp" alt="Miles Maggio" />
                      <AuthorDetails>
                        <AuthorName>Miles Maggio</AuthorName>
                        <AuthorBio>top voice, feat. speaker, and advisor on AI agent engineering.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>

                    <div id="comments-paypal-agentic-commerce-inflection" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Be the first to comment on this post.</p>
                    </div>

                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('paypal-agentic-commerce-inflection')} className="like-button-paypal-agentic-commerce-inflection">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>

                      <InteractionButton onClick={() => handleComment('paypal-agentic-commerce-inflection')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>

                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('paypal-agentic-commerce-inflection')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>

                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('paypal-agentic-commerce-inflection')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>

                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>

                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>

                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>

                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>

                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>

                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>

                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                            </svg>
                          </EmailButton>

                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z" />
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>

                    <FullPostLink
                      to="/insights/post/paypal-agentic-commerce-inflection"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('paypal-agentic-commerce-inflection');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'ai-agents-current-trends') {
                    return (
                      <React.Fragment key={post.id}>
                {/* New blog post - The State of AI Agents and Current Trends */}
                <BlogFullPost id="ai-agents-current-trends">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>technology</BlogPostCategoryTag>
                      <BlogPostCategoryTag>market analysis</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>The State of AI Agents and Current Trends</BlogPostHeading>
                    
                    <TopAuthorInfo>
                      <TopAuthorAvatar src="https://randomuser.me/api/portraits/women/44.jpg" alt="Author" />
                      <TopAuthorDetails>
                        <TopAuthorName>Emma Richardson</TopAuthorName>
                        <TopAuthorRole>Senior AI Analyst</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>
                    
                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      May 14, 2025
                    </BlogPostHeaderDate>
                    
                    <BlogPostImageContainer>
                      <BlogPostImg 
                        src="https://images.unsplash.com/photo-1589254065878-42c9da997008?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                        alt="AI agents analysis illustration"
                      />
                      <BlogPostCaption>
                        AI agents represent one of the most transformative technological developments of the decade, reshaping industries through autonomous decision-making.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>
                  
                  <BlogPostSection>
                    <BlogPostText>
                      AI agents represent one of the most transformative technological developments of the decade, reshaping industries through autonomous decision-making, task execution, and continuous learning. As of 2025, these systems have evolved beyond theoretical concepts into practical tools driving measurable economic value. This article provides a comprehensive analysis of the AI agent landscape, grounded in quantitative data, real-world applications, and actionable insights for entrepreneurs and builders.
                    </BlogPostText>
                    
                    <BlogPostSubheading>Defining AI Agents in 2025</BlogPostSubheading>
                    <BlogPostText>
                      AI agents are autonomous software systems that perceive their environment, reason through problems, and take actions to achieve specific goals with minimal human intervention. Unlike traditional automation tools or chatbots, modern AI agents integrate large language models (LLMs), advanced planning algorithms, and domain-specific skills to handle complex workflows. For example, enterprise AI agents can now resolve IT support tickets end-to-end, analyze legal contracts for compliance risks, or optimize supply chain logistics in real time.
                    </BlogPostText>
                    
                    <BlogPostText>
                      Three characteristics distinguish true AI agents:
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Goal-oriented autonomy</strong>: They independently break down objectives into actionable steps.</li>
                      <li><strong>Dynamic adaptation</strong>: They learn from interactions and adjust strategies without reprogramming.</li>
                      <li><strong>Tool orchestration</strong>: They seamlessly integrate with APIs, databases, and physical systems to execute tasks.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Quantitative Impact of the AI Agent Revolution</BlogPostSubheading>

                    {/* Provenance line added 2026-09-11. The nineteen figures below are
                        third-party industry projections, and sitting unlabelled on a
                        vendor's own blog they read as prest0's measured results — which
                        is the implication the FTC's substantiation rules reach, whoever
                        first published the number. Saying whose they are, and that they
                        are not ours, removes that implication without gutting the piece.
                        Cite each one properly, or cut it, before repeating any of them
                        anywhere that sells something. */}
                    <BlogPostText>
                      <em>
                        The figures in this section are published industry projections and
                        third-party market research about AI agents generally. They are not
                        prest0's own results, not measurements of this product, and not a
                        prediction of what any particular team will see.
                      </em>
                    </BlogPostText>

                    <BlogPostText>
                      <strong>Market Growth and Adoption</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li>The global AI agent market was valued at <strong>$5.1 billion in 2024</strong>, with projections reaching <strong>$47 billion by 2030</strong> (44% CAGR). Conservative estimates suggest it could surpass <strong>$139 billion by 2033</strong> as multi-agent systems proliferate.</li>
                      <li><strong>85% of enterprises</strong> plan to deploy AI agents by 2025, with early adopters reporting <strong>50% efficiency gains</strong> in customer service and supply chain operations.</li>
                      <li>North America leads adoption (38% market share), but Asia-Pacific is growing rapidly at <strong>49% CAGR</strong>, driven by manufacturing and healthcare applications.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>ROI and Business Value</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li>Customer service agents reduce resolution times by <strong>70%</strong> while cutting costs by <strong>40%</strong> compared to human teams.</li>
                      <li>Healthcare diagnostic agents now achieve <strong>92% accuracy</strong> in analyzing medical imaging, reducing radiologists' workload by <strong>30%</strong>.</li>
                      <li>Financial fraud detection agents have lowered false positives by <strong>65%</strong> while identifying <strong>28% more suspicious transactions</strong>.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Application Layer: Where AI Agents Deliver Value Today</BlogPostSubheading>
                    
                    <BlogPostText>
                      <strong>Enterprise Agents</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>IT support agents</strong> like Moveworks resolve <strong>88% of employee tickets</strong> autonomously by integrating with ServiceNow, Slack, and Jira.</li>
                      <li><strong>Sales orchestration agents</strong> analyze CRM data, draft personalized outreach, and book follow-up meetings, boosting lead conversion by <strong>25%</strong>.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>Industry-Specific Agents</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Healthcare</strong>:
                        <ul>
                          <li>Mayo Clinic's patient intake agent reduces registration errors by <strong>90%</strong> while cutting wait times.</li>
                          <li>Drug discovery agents simulate <strong>10,000+ molecular interactions daily</strong>, accelerating R&D cycles.</li>
                        </ul>
                      </li>
                      <li><strong>Manufacturing</strong>:
                        <ul>
                          <li>Predictive maintenance agents at Siemens analyze sensor data to prevent <strong>73% of equipment failures</strong>.</li>
                          <li>Inventory optimization agents reduce waste by <strong>35%</strong> through real-time demand forecasting.</li>
                        </ul>
                      </li>
                      <li><strong>Financial Services</strong>:
                        <ul>
                          <li>JPMorgan's COiN agent reviews <strong>12,000 commercial contracts annually</strong>, saving <strong>360,000 work hours</strong>.</li>
                          <li>Wealth management agents at Betterment personalize portfolios using real-time market data, outperforming human advisors by <strong>15%</strong>.</li>
                        </ul>
                      </li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>Consumer-Facing Agents</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li>Travel agents like Kayak's price tracker monitor fares across <strong>200+ airlines</strong>, saving users <strong>$500+ annually</strong>.</li>
                      <li>Shopping agents (e.g., Amazon's Rufus) compare prices, read reviews, and automate purchases, with <strong>32% of Gen Z</strong> using them weekly.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Timeline for AI Agent Evolution</BlogPostSubheading>
                    
                    <BlogPostText>
                      <strong>Short-Term (2024–2026)</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Single-task agents</strong> dominate: Focused on specific workflows (e.g., customer onboarding, inventory restocking).</li>
                      <li><strong>Low-code platforms</strong> emerge: Tools like Zapier's AI Agent Builder enable non-technical users to create basic agents.</li>
                      <li><strong>Regulatory frameworks</strong> solidify: GDPR-style rules for AI transparency and accountability.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>Mid-Term (2026–2030)</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Multi-agent systems</strong> become standard: Collaborative networks where agents specialize in sub-tasks (e.g., a marketing campaign agent working with content, analytics, and compliance agents).</li>
                      <li><strong>Edge computing integration</strong>: Agents operate on IoT devices with <strong>&lt;10ms latency</strong> for real-time factory/vehicle decisions.</li>
                      <li><strong>Emotional intelligence</strong>: Agents detect frustration in customer calls and escalate issues 40% faster.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>Long-Term (2030+)</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Self-improving agents</strong>: Systems that optimize their codebase, achieving <strong>100x efficiency gains</strong> over human developers.</li>
                      <li><strong>Ubiquitous agents</strong>: Over <strong>50% of digital interactions</strong> mediated by AI agents across healthcare, education, and governance.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Strategic Opportunities for Builders</BlogPostSubheading>
                    
                    <BlogPostText>
                      <strong>1. Vertical AI Agent Development</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Problem</strong>: Generic agents struggle with industry-specific jargon/processes.</li>
                      <li><strong>Opportunity</strong>: Build agents for niche domains like construction permitting or clinical trial matching. The legal agent market alone is projected to hit <strong>$8.4 billion by 2027</strong>.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>2. Agent Infrastructure</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Tooling gaps</strong>: Only <strong>12% of enterprises</strong> have the MLops pipelines needed to maintain agents.</li>
                      <li><strong>High-demand solutions</strong>:
                        <ul>
                          <li>Testing frameworks for agent reliability</li>
                          <li>Cross-platform orchestration engines</li>
                          <li>Ethical AI monitoring tools</li>
                        </ul>
                      </li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>3. Hybrid Human-Agent Workflows</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Example</strong>: A real estate agent that handles paperwork (80% of tasks) while humans focus on client relationships.</li>
                      <li><strong>Market potential</strong>: <strong>$6.2 billion</strong> by 2026 for collaboration platforms.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Implementation Considerations</BlogPostSubheading>
                    
                    <BlogPostText>
                      <strong>Integration Strategies</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>API-first design</strong>: 78% of successful agents use modular architectures for easy Salesforce/Workday integration.</li>
                      <li><strong>Data pipelines</strong>: Invest in unified data lakes-agents using fragmented sources underperform by <strong>43%</strong>.</li>
                    </BlogPostList>
                    
                    <BlogPostText>
                      <strong>Security and Ethics</strong>
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Zero-trust architectures</strong>: Required for healthcare/finance agents handling sensitive data.</li>
                      <li><strong>Bias mitigation</strong>: Regularly audit training data-unchecked agents make <strong>7x more discriminatory loan decisions</strong>.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Conclusion</BlogPostSubheading>
                    <BlogPostText>
                      The AI agent revolution is transitioning from hype to tangible value, with measurable impacts across every major industry. For entrepreneurs, the key opportunities lie in vertical-specific solutions, robust infrastructure tools, and ethical implementation frameworks. As agents evolve from single-task tools to collaborative networks, builders who prioritize adaptability and domain expertise will lead the next wave of innovation.
                    </BlogPostText>
                    
                    <Divider />
                    
                    <Citation>
                      Citations:
                      <CitationList>
                        <li><a href="https://www.semanticscholar.org/paper/aca83fcfffd8790649896f9bbbc5331b01ed0d2c" target="_blank" rel="noreferrer">Revolutionizing Supply Chain Management with AI Agents on DataBricks</a></li>
                        <li><a href="https://arxiv.org/abs/2502.00964" target="_blank" rel="noreferrer">ML-Dev-Bench: Comparative Analysis of AI Agents on ML development workflows</a></li>
                        <li><a href="https://arxiv.org/abs/2503.16041" target="_blank" rel="noreferrer">GreenIQ: A Deep Search Platform for Comprehensive Carbon Market Analysis and Automated Report Generation</a></li>
                        <li><a href="https://arxiv.org/abs/2505.01560" target="_blank" rel="noreferrer">AI agents may be worth the hype but not the resources (yet): An initial exploration of machine translation quality and costs in three language pairs in the legal and news domains</a></li>
                        <li><a href="https://www.semanticscholar.org/paper/866698eb4f67c3f46bb4944e5f7d719b5525bf8d" target="_blank" rel="noreferrer">Pragmatic AI Approach for Enhanced Well Planning and Execution: Leveraging AI Agents to Automate Workflows, Reduce Human Errors, and Improve Decision-Making Quality</a></li>
                        <li><a href="https://www.semanticscholar.org/paper/3018e66e35a4ce56cc1d868f6df3356910fe10fc" target="_blank" rel="noreferrer">Will 2025 be the year of the agent? A primer for competition practitioners on the next wave of AI innovation</a></li>
                        <li><a href="https://arxiv.org/abs/2504.07139" target="_blank" rel="noreferrer">Artificial Intelligence Index Report 2025</a></li>
                        <li><a href="https://www.semanticscholar.org/paper/4bde96e81d9f7f95c16a3ba967babd02015fa261" target="_blank" rel="noreferrer">AI-Driven Sentiment Analysis for Bitcoin Market Trends: A Predictive Approach to Crypto Volatility</a></li>
                        <li><a href="https://www.globenewswire.com/news-release/2025/05/12/3078843/0/en/AI-Agents-Market-Report-2025-Ready-to-Deploy-AI-Agents-Capture-Market-Share-Customize-Solutions-on-the-Rise.html" target="_blank" rel="noreferrer">AI Agents Market Report 2025: Ready to Deploy AI Agents Capture Market Share, Customize Solutions on the Rise</a></li>
                        <li><a href="https://www.alvarezandmarsal.com/thought-leadership/demystifying-ai-agents-in-2025-separating-hype-from-reality-and-navigating-market-outlook" target="_blank" rel="noreferrer">Demystifying AI Agents in 2025: Separating Hype From Reality and Navigating Market Outlook</a></li>
                      </CitationList>
                    </Citation>
                    
                    <AuthorInfo>
                      <AuthorAvatar src="https://randomuser.me/api/portraits/women/44.jpg" alt="Author" />
                      <AuthorDetails>
                        <AuthorName>Emma Richardson</AuthorName>
                        <AuthorBio>Emma is our senior AI analyst with over 10 years of experience in emerging technologies. She specializes in the intersection of artificial intelligence and business strategy.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>
                    
                    {/* Add a comments section */}
                    <div id="comments-ai-agents-current-trends" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Be the first to comment on this post.</p>
                    </div>
                    
                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agents-current-trends')} className="like-button-ai-agents-current-trends">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>
                      
                      <InteractionButton onClick={() => handleComment('ai-agents-current-trends')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>
                      
                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agents-current-trends')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>
                      
                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agents-current-trends')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>
                        
                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>
                          
                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>
                          
                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>
                          
                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>
                          
                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>
                          
                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>
                          
                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                          </EmailButton>
                          
                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z"/>
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>
                    
                    <FullPostLink 
                      to="/insights/post/ai-agents-current-trends" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agents-current-trends');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                    
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  if (post.id === 'ai-agent-revolution') {
                    return (
                      <React.Fragment key={post.id}>
                <BlogFullPost id="ai-agent-revolution">
                  <BlogPostHeader>
                    <BlogPostCategories>
                      <BlogPostCategoryTag>artificial intelligence</BlogPostCategoryTag>
                      <BlogPostCategoryTag>technology</BlogPostCategoryTag>
                      <BlogPostCategoryTag>future of work</BlogPostCategoryTag>
                    </BlogPostCategories>
                    <BlogPostHeading>The Value of the Coming AI Agent Revolution</BlogPostHeading>
                    
                    <TopAuthorInfo>
                      <TopAuthorAvatar src="https://randomuser.me/api/portraits/women/44.jpg" alt="Author" />
                      <TopAuthorDetails>
                        <TopAuthorName>Emma Richardson</TopAuthorName>
                        <TopAuthorRole>Senior AI Analyst</TopAuthorRole>
                      </TopAuthorDetails>
                    </TopAuthorInfo>
                    
                    <BlogPostHeaderDate>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      May 12, 2025
                    </BlogPostHeaderDate>
                    
                    <BlogPostImageContainer>
                      <BlogPostImg 
                        src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                        alt="AI agents concept illustration"
                      />
                      <BlogPostCaption>
                        AI agents are reshaping how we interact with technology and automate complex workflows.
                      </BlogPostCaption>
                    </BlogPostImageContainer>
                  </BlogPostHeader>
                  
                  <BlogPostSection>
                    <BlogPostSubheading>What Are AI Agents?</BlogPostSubheading>
                    <BlogPostText>
                      AI agents are autonomous software systems that use artificial intelligence to perceive their environment, make decisions, and perform tasks on behalf of users or organizations with minimal human oversight. They demonstrate capabilities such as reasoning, planning, learning, and memory, and can operate independently to achieve specific goals. Types of AI agents range from simple reflex agents (reactive, rule-based) to advanced hierarchical agents that can coordinate complex, multistep workflows.
                    </BlogPostText>
                    
                    <BlogPostSubheading>Quantitative Value: Market Size and Growth</BlogPostSubheading>
                    <BlogPostText>
                      The AI agent revolution is not just theoretical—its economic impact is already unfolding at a rapid pace:
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Market Size:</strong> In 2024, the global market for agentic AI was valued at $5.1 billion. By 2030, forecasts project this will surpass $47 billion, reflecting a compound annual growth rate (CAGR) of over 44%. Other estimates are even more bullish, with some projecting the AI agents market to reach $139.12 billion by 2033, up from $3.66 billion in 2023, at a CAGR of 43.88%.</li>
                      <li><strong>Adoption:</strong> North America currently leads the market, holding nearly 38% of global share.</li>
                      <li><strong>Consumer Readiness:</strong> 39% of consumers are already comfortable with AI agents handling tasks like scheduling, and 24% would let AI agents conduct their shopping—rising to 32% among Gen Z.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Where Is the Value Generated?</BlogPostSubheading>
                    <BlogPostText>
                      AI agents are transforming value creation across several dimensions:
                    </BlogPostText>
                    
                    <BlogPostList>
                      <li><strong>Productivity and Efficiency:</strong> AI agents automate both routine and complex tasks, allowing human workers to focus on strategic, creative, or interpersonal activities. This shift reduces operational costs and minimizes human error.</li>
                      <li><strong>Customer Experience:</strong> Over a third of consumers prefer AI agents for repetitive interactions, and 37% value personalized content generated by AI agents. This highlights their impact on customer satisfaction and retention.</li>
                      <li><strong>Business Agility:</strong> Ready-to-deploy AI agents now dominate the market, making it easier for businesses to implement solutions quickly and scale as needed.</li>
                      <li><strong>Industry Penetration:</strong> Sectors such as customer service, healthcare, finance, and logistics are already integrating AI agents for everything from virtual assistants to automated inventory management.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Timeline: When Will AI Agents Deliver Value?</BlogPostSubheading>
                    
                    <BlogPostList>
                      <li><strong>Short-Term (2024–2026):</strong> AI agents are already being deployed in customer service, scheduling, and e-commerce. Adoption is accelerating as tools become easier to set up and integrate, especially with advances in generative AI.</li>
                      <li><strong>Mid-Term (2026–2030):</strong> Expect exponential growth in both market size and sophistication. AI agents will increasingly handle complex, multistep workflows and collaborate with other agents and humans, moving from "thought to action".</li>
                      <li><strong>Long-Term (2030+):</strong> AI agents will become foundational to digital business operations, with multi-agent systems orchestrating entire business processes across industries.</li>
                    </BlogPostList>
                    
                    <BlogPostSubheading>Why Should Entrepreneurs and Builders Care?</BlogPostSubheading>
                    
                    <BlogPostList>
                      <li><strong>Massive Market Opportunity:</strong> The projected market growth (over 40% CAGR) signals vast opportunities for startups and established companies to build, deploy, or integrate AI agent solutions.</li>
                      <li><strong>Lower Barriers to Entry:</strong> Advances in foundation models and ready-to-deploy agents mean it's easier than ever to experiment, iterate, and scale AI agent-based products.</li>
                      <li><strong>Changing Consumer Expectations:</strong> A growing share of consumers—especially younger demographics—are ready to interact with AI agents, opening up new business models and service paradigms.</li>
                      <li><strong>Competitive Differentiation:</strong> Early adopters can leverage AI agents for cost savings, better customer experiences, and operational agility, gaining a significant edge in their respective markets.</li>
                    </BlogPostList>
                    
                    <BlogPostTable>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Metric/Aspect</th>
                            <th>Value/Trend (2024–2033)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Global AI Agent Market Size</td>
                            <td>$5.1B (2024) → $47B (2030) → $139B (2033)</td>
                          </tr>
                          <tr>
                            <td>Compound Annual Growth Rate (CAGR)</td>
                            <td>43–44%</td>
                          </tr>
                          <tr>
                            <td>Leading Region</td>
                            <td>North America (38% market share)</td>
                          </tr>
                          <tr>
                            <td>Consumer Comfort with AI Agents</td>
                            <td>39% (scheduling), 24% (shopping), 32% Gen Z</td>
                          </tr>
                          <tr>
                            <td>Key Use Cases</td>
                            <td>Customer service, automation, personalization, workflow orchestration</td>
                          </tr>
                          <tr>
                            <td>Deployment Timeline</td>
                            <td>Now (simple agents) → 2026+ (complex, multi-agent systems)</td>
                          </tr>
                        </tbody>
                      </StyledTable>
                    </BlogPostTable>
                    
                    <BlogPostSubheading>Conclusion</BlogPostSubheading>
                    <BlogPostText>
                      The AI agent revolution is already underway. With rapid advances in generative AI and foundation models, AI agents are moving from simple automation to orchestrating complex, value-generating workflows across industries. The quantitative projections—over $100 billion in market value within a decade—underscore the scale of the opportunity. For entrepreneurs and builders, now is the time to engage, experiment, and capture value as AI agents reshape the digital economy.
                    </BlogPostText>
                    
                    <Divider />
                    
                    <Citation>
                      Citations:
                      <CitationList>
                        <li><a href="https://cloud.google.com/discover/what-are-ai-agents" target="_blank" rel="noreferrer">https://cloud.google.com/discover/what-are-ai-agents</a></li>
                        <li><a href="https://www.ibm.com/think/topics/ai-agents" target="_blank" rel="noreferrer">https://www.ibm.com/think/topics/ai-agents</a></li>
                        <li><a href="https://www.moveworks.com/us/en/resources/blog/what-is-an-ai-agent" target="_blank" rel="noreferrer">https://www.moveworks.com/us/en/resources/blog/what-is-an-ai-agent</a></li>
                        <li><a href="https://www.newhorizons.com/resources/blog/types-of-ai-agents" target="_blank" rel="noreferrer">https://www.newhorizons.com/resources/blog/types-of-ai-agents</a></li>
                        <li><a href="https://www.datacamp.com/blog/types-of-ai-agents" target="_blank" rel="noreferrer">https://www.datacamp.com/blog/types-of-ai-agents</a></li>
                        <li><a href="https://www.statista.com/statistics/1552183/global-agentic-ai-market-value/" target="_blank" rel="noreferrer">https://www.statista.com/statistics/1552183/global-agentic-ai-market-value/</a></li>
                        <li><a href="https://www.zdnet.com/article/ai-agents-are-the-next-frontier-and-will-change-our-working-lives-forever/" target="_blank" rel="noreferrer">https://www.zdnet.com/article/ai-agents-are-the-next-frontier-and-will-change-our-working-lives-forever/</a></li>
                        <li><a href="https://www.sap.com/resources/what-are-ai-agents" target="_blank" rel="noreferrer">https://www.sap.com/resources/what-are-ai-agents</a></li>
                        <li><a href="https://market.us/report/ai-agents-market/" target="_blank" rel="noreferrer">https://market.us/report/ai-agents-market/</a></li>
                      </CitationList>
                    </Citation>
                    
                    <AuthorInfo>
                      <AuthorAvatar src="https://randomuser.me/api/portraits/women/44.jpg" alt="Author" />
                      <AuthorDetails>
                        <AuthorName>Emma Richardson</AuthorName>
                        <AuthorBio>Emma is our senior AI analyst with over 10 years of experience in emerging technologies. She specializes in the intersection of artificial intelligence and business strategy.</AuthorBio>
                      </AuthorDetails>
                    </AuthorInfo>
                    
                    {/* Add a comments section */}
                    <div id="comments-ai-agent-revolution" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--brand-charcoal)' }}>Comments</h3>
                      <p style={{ color: 'var(--brand-text-secondary)', fontStyle: 'italic' }}>Be the first to comment on this post.</p>
                    </div>
                    
                    <BlogPostInteractions>
                      <InteractionButton $active onClick={() => handleLike('ai-agent-revolution')} className="like-button-ai-agent-revolution">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        Like
                      </InteractionButton>
                      
                      <InteractionButton onClick={() => handleComment('ai-agent-revolution')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                        Comment
                      </InteractionButton>
                      
                      <InteractionButton $active={isBookmarked} onClick={() => handleBookmark('ai-agent-revolution')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        Bookmark
                      </InteractionButton>
                      
                      <ShareOptionsContainer>
                        <ShareButton className="share-button" onClick={() => handleShare('ai-agent-revolution')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" />
                            <circle cx="6" cy="12" r="3" />
                            <circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                          </svg>
                          Share
                        </ShareButton>
                        
                        <ShareMenu isOpen={isShareMenuOpen} ref={shareMenuRef}>
                          <FacebookButton onClick={handleShareFacebook} title="Share on Facebook">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                            </svg>
                          </FacebookButton>
                          
                          <TwitterButton onClick={handleShareTwitter} title="Share on Twitter">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                            </svg>
                          </TwitterButton>
                          
                          <LinkedinButton onClick={handleShareLinkedin} title="Share on LinkedIn">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                              <rect x="2" y="9" width="4" height="12" />
                              <circle cx="4" cy="4" r="2" />
                            </svg>
                          </LinkedinButton>
                          
                          <RedditButton onClick={handleShareReddit} title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M17 12a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-8-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm10.83.5c0-1.04-.84-1.88-1.88-1.88-.35 0-.68.1-.97.27-1.12-.8-2.62-1.31-4.27-1.37l.85-3.03 2.58.59c.05.62.56 1.1 1.19 1.1.66 0 1.2-.54 1.2-1.2 0-.66-.54-1.2-1.2-1.2-.46 0-.84.26-1.05.63l-2.87-.66a.32.32 0 0 0-.38.22l-.98 3.5c-1.72.05-3.3.57-4.48 1.4a1.85 1.85 0 0 0-.97-.28 1.88 1.88 0 0 0-1.88 1.88c0 .8.5 1.48 1.2 1.76-.04.23-.07.46-.07.7 0 2.53 2.95 4.58 6.58 4.58 3.64 0 6.58-2.05 6.58-4.58 0-.23-.02-.45-.06-.67a1.88 1.88 0 0 0 1.2-1.76ZM8.2 14.76c.08.07.17.12.26.12.1 0 .19-.04.27-.12.36-.36.95-2.04 1.1-2.36-.15-.32-.74-2-.1-2.36-.15-.15-.4-.15-.55 0-.36.36-1.3 2.05-1.3 2.36 0 .31.95 2 1.31 2.36Zm6.67-2.36c0-.31-.94-2-1.3-2.36a.38.38 0 0 0-.56 0c-.36.37-.95 2.05-.95 2.36 0 .31.59 2 .95 2.36.08.08.17.12.27.12.1 0 .18-.04.26-.12.37-.36 1.33-2.04 1.33-2.36Zm.9 2.57c-.12 0-.21.05-.29.13-.8.08-.12.17-.12.28 0 .1.04.2.12.28.07.08.17.12.27.12.11 0 .21-.04.28-.12a.38.38 0 0 0 .13-.28.4.4 0 0 0-.12-.28.4.4 0 0 0-.29-.13Zm-7.48-2.5c-.22 0-.4.18-.4.4s.18.4.4.4.4-.18.4-.4-.18-.4-.4-.4Z" />
                            </svg>
                          </RedditButton>
                          
                          <WhatsappButton onClick={handleShareWhatsapp} title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                          </WhatsappButton>
                          
                          <TelegramButton onClick={handleShareTelegram} title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                            </svg>
                          </TelegramButton>
                          
                          <EmailButton onClick={handleShareEmail} title="Share via Email">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                          </EmailButton>
                          
                          <CopyLinkButton onClick={handleCopyLink} title="Copy Link">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 11h8v2H8v-2zm12.1 1H22c0-2.76-2.24-5-5-5h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1zM3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM19 7h2v10h-2V7z"/>
                            </svg>
                          </CopyLinkButton>
                        </ShareMenu>
                      </ShareOptionsContainer>
                    </BlogPostInteractions>
                    
                    <FullPostLink 
                      to="/insights/post/ai-agent-revolution"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewFullPost('ai-agent-revolution');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      View Post
                    </FullPostLink>
                  </BlogPostSection>
                </BlogFullPost>
                      </React.Fragment>
                    );
                  }
                  
                  return null;
                })}

                {selectedCategory && filteredPosts.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '4rem 2rem', 
                    color: 'var(--brand-text-secondary)',
                    fontSize: '1.1rem'
                  }}>
                    no posts found for category "{selectedCategory}"
                  </div>
                )}
                
                {/* Pagination for when we have posts */}
                {false && (
                  <Pagination>
                    <PaginationButton disabled>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </PaginationButton>
                    <PaginationButton $active>1</PaginationButton>
                    <PaginationButton>2</PaginationButton>
                    <PaginationButton>3</PaginationButton>
                    <PaginationButton>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </PaginationButton>
                  </Pagination>
                )}
              </>
            )}
          </section>
        </MainContent>
        
        <Sidebar>
          {/* Categories Section */}
          <SidebarSection>
            <SidebarTitle>categories</SidebarTitle>
            <CategoryList>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('artificial intelligence');
                  }}
                  style={{
                    background: selectedCategory === 'artificial intelligence' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'artificial intelligence' ? 600 : 500
                  }}
                >
                  <span>artificial intelligence</span>
                  <span>15</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('video spotlight');
                  }}
                  style={{
                    background: selectedCategory === 'video spotlight' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'video spotlight' ? 600 : 500
                  }}
                >
                  <span>video spotlight</span>
                  <span>4</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('technology');
                  }}
                  style={{
                    background: selectedCategory === 'technology' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'technology' ? 600 : 500
                  }}
                >
                  <span>technology</span>
                  <span>3</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('enterprise AI');
                  }}
                  style={{
                    background: selectedCategory === 'enterprise AI' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'enterprise AI' ? 600 : 500
                  }}
                >
                  <span>enterprise AI</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('agent engineering');
                  }}
                  style={{
                    background: selectedCategory === 'agent engineering' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'agent engineering' ? 600 : 500
                  }}
                >
                  <span>agent engineering</span>
                  <span>8</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('cybersecurity');
                  }}
                  style={{
                    background: selectedCategory === 'cybersecurity' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'cybersecurity' ? 600 : 500
                  }}
                >
                  <span>cybersecurity</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('market analysis');
                  }}
                  style={{
                    background: selectedCategory === 'market analysis' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'market analysis' ? 600 : 500
                  }}
                >
                  <span>market analysis</span>
                  <span>8</span>
                </CategoryLink>
              </CategoryItem>

              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('business strategy');
                  }}
                  style={{
                    background: selectedCategory === 'business strategy' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'business strategy' ? 600 : 500
                  }}
                >
                  <span>business strategy</span>
                  <span>3</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('business opportunity');
                  }}
                  style={{
                    background: selectedCategory === 'business opportunity' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'business opportunity' ? 600 : 500
                  }}
                >
                  <span>business opportunity</span>
                  <span>2</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('founder insights');
                  }}
                  style={{
                    background: selectedCategory === 'founder insights' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'founder insights' ? 600 : 500
                  }}
                >
                  <span>founder insights</span>
                  <span>4</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('agent infrastructure');
                  }}
                  style={{
                    background: selectedCategory === 'agent infrastructure' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'agent infrastructure' ? 600 : 500
                  }}
                >
                  <span>agent infrastructure</span>
                  <span>2</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('energy infrastructure');
                  }}
                  style={{
                    background: selectedCategory === 'energy infrastructure' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'energy infrastructure' ? 600 : 500
                  }}
                >
                  <span>energy infrastructure</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('identity security');
                  }}
                  style={{
                    background: selectedCategory === 'identity security' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'identity security' ? 600 : 500
                  }}
                >
                  <span>identity security</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('agentic commerce');
                  }}
                  style={{
                    background: selectedCategory === 'agentic commerce' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'agentic commerce' ? 600 : 500
                  }}
                >
                  <span>agentic commerce</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('payments');
                  }}
                  style={{
                    background: selectedCategory === 'payments' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'payments' ? 600 : 500
                  }}
                >
                  <span>payments</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('memory systems');
                  }}
                  style={{
                    background: selectedCategory === 'memory systems' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'memory systems' ? 600 : 500
                  }}
                >
                  <span>memory systems</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('funding news');
                  }}
                  style={{
                    background: selectedCategory === 'funding news' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'funding news' ? 600 : 500
                  }}
                >
                  <span>funding news</span>
                  <span>1</span>
                </CategoryLink>
              </CategoryItem>
              <CategoryItem>
                <CategoryLink 
                  as="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleCategoryClick('future of work');
                  }}
                  style={{
                    background: selectedCategory === 'future of work' 
                      ? 'rgba(198, 167, 94, 0.15)' 
                      : 'transparent',
                    fontWeight: selectedCategory === 'future of work' ? 600 : 500
                  }}
                >
                  <span>future of work</span>
                  <span>3</span>
                </CategoryLink>
              </CategoryItem>
            </CategoryList>
          </SidebarSection>
          
          {/* Popular Posts Section */}
          <SidebarSection>
            <SidebarTitle>latest posts</SidebarTitle>
            <PopularPostList>
              <PopularPost key="featured-ai-mass-layoff" to="#latest" onClick={() => handleViewFullPost('ai-mass-layoff-reckoning')}>
                <PopularPostImage $imageSrc={AI_MASS_LAYOFF_IMAGE} $hasVideo />
                <PopularPostContent>
                  <PopularPostTitle>the ai mass layoff reckoning</PopularPostTitle>
                  <PopularPostMeta>november 10, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="featured-is-ai-bubble" to="#latest" onClick={() => handleViewFullPost('is-ai-in-a-bubble')}>
                <PopularPostImage $imageSrc={IS_AI_BUBBLE_IMAGE} $hasVideo />
                <PopularPostContent>
                  <PopularPostTitle>is ai in a bubble? (time to prepare is now)</PopularPostTitle>
                  <PopularPostMeta>november 8, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="featured-ai-agents-replace-you" to="#latest" onClick={() => handleViewFullPost('ai-agents-will-replace-you-unless-you-do-this')}>
                <PopularPostImage $imageSrc={AI_AGENTS_REPLACE_YOU_IMAGE} $hasVideo />
                <PopularPostContent>
                  <PopularPostTitle>ai agents will replace you unless you do this</PopularPostTitle>
                  <PopularPostMeta>november 6, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="featured-ai-infrastructure" to="#latest" onClick={() => handleViewFullPost('ai-infrastructure-bottleneck')}>
                <PopularPostImage $imageSrc={AI_INFRASTRUCTURE_BOTTLENECK_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the AI infrastructure bottleneck keeping me up at night</PopularPostTitle>
                  <PopularPostMeta>november 6, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="0" to="#latest" onClick={() => handleViewFullPost('ai-agent-security-crisis-nobody-talking-about')}>
                <PopularPostImage $imageSrc={AI_AGENT_SECURITY_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the #1 security crisis nobody's talking about</PopularPostTitle>
                  <PopularPostMeta>november 5, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="1" to="#latest" onClick={() => handleViewFullPost('ai-agent-opportunity-nobody-talking-about-yet')}>
                <PopularPostImage $imageSrc={AI_AGENT_OPPORTUNITY_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the AI agent opportunity nobody is talking about yet</PopularPostTitle>
                  <PopularPostMeta>november 4, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="2" to="#latest" onClick={() => handleViewFullPost('agentic-ai-window-closing-fast')}>
                <PopularPostImage $imageSrc={AGENTIC_WINDOW_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the agentic AI window just opened. it's closing faster than you think.</PopularPostTitle>
                  <PopularPostMeta>november 2, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="3" to="#latest" onClick={() => handleViewFullPost('openai-trillion-dollar-ipo-starting-gun')}>
                <PopularPostImage $imageSrc={OPENAI_IPO_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>OpenAI's $1 trillion ipo just fired the starting gun</PopularPostTitle>
                  <PopularPostMeta>november 1, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="4" to="#latest" onClick={() => handleViewFullPost('ai-agent-app-store-phase')}>
                <PopularPostImage $imageSrc={AI_AGENT_APP_STORE_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the AI agent revolution just entered the "app store" phase</PopularPostTitle>
                  <PopularPostMeta>october 31, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="5" to="#latest" onClick={() => handleViewFullPost('secret-hidden-ai-revolution')}>
                <PopularPostImage $imageSrc={SECRET_HIDDEN_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the secret about ai agents hidden in plain sight</PopularPostTitle>
                  <PopularPostMeta>october 30, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="6" to="#latest" onClick={() => handleViewFullPost('claude-skills-enterprise-inflection')}>
                <PopularPostImage $imageSrc={CLAUDE_SKILLS_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the enterprise AI agent inflection point nobody's talking about</PopularPostTitle>
                  <PopularPostMeta>october 29, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="7" to="#latest" onClick={() => handleViewFullPost('mem0-memory-infrastructure')}>
                <PopularPostImage $imageSrc={MEM0_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>why agent memory infrastructure unlocks your largest opportunity</PopularPostTitle>
                  <PopularPostMeta>october 29, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="8" to="#latest" onClick={() => handleViewFullPost('ai-agent-inflection-point-video')}>
                <PopularPostImage $imageSrc={AI_AGENT_VIDEO_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>the ai agent inflection point (video)</PopularPostTitle>
                  <PopularPostMeta>october 28, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="9" to="#latest" onClick={() => handleViewFullPost('paypal-agentic-commerce-inflection')}>
                <PopularPostImage $imageSrc={PAYPAL_AGENTIC_IMAGE} />
                <PopularPostContent>
                  <PopularPostTitle>paypal's agentic commerce inflection point</PopularPostTitle>
                  <PopularPostMeta>october 28, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="10" to="#latest" onClick={() => handleViewFullPost('ai-agents-current-trends')}>
                <PopularPostImage $imageSrc="https://images.unsplash.com/photo-1589254065878-42c9da997008?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" />
                <PopularPostContent>
                  <PopularPostTitle>the state of ai agents and current trends</PopularPostTitle>
                  <PopularPostMeta>may 14, 2025</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="11" to="#">
                <PopularPostImage $imageSrc="https://images.unsplash.com/photo-1633412802994-5c058f151b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" />
                <PopularPostContent>
                  <PopularPostTitle>prerequisite concepts for developing ai agents</PopularPostTitle>
                  <PopularPostMeta>coming soon</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
              <PopularPost key="12" to="#">
                <PopularPostImage $imageSrc="https://images.unsplash.com/photo-1607799279861-4dd421887fb3?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" />
                <PopularPostContent>
                  <PopularPostTitle>dynamic graph rag whitepaper</PopularPostTitle>
                  <PopularPostMeta>coming soon</PopularPostMeta>
                </PopularPostContent>
              </PopularPost>
            </PopularPostList>
          </SidebarSection>
          
          {/* Subscribe Section */}
          <SidebarSection id="subscribe">
            <SidebarTitle>subscribe</SidebarTitle>
            <SubscriptionText>
              subscribe to our newsletter to get the latest updates directly to your inbox.
            </SubscriptionText>
            <SubscribeForm onSubmit={handleSubscribe}>
              <SubscribeInput 
                type="email" 
                placeholder="enter your email" 
                value={subscriberEmail}
                onChange={(e) => setSubscriberEmail(e.target.value)}
                required
              />
              <SubscribeButton type="submit">subscribe</SubscribeButton>
            </SubscribeForm>
          </SidebarSection>
        </Sidebar>
      </BlogContent>
      
      {/* Navigation Sidebar */}
      <NavigationBar>
        <NavItem 
          href="#latest" 
          $active={activeSection === 'latest'}
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('latest');
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          latest
        </NavItem>
        <NavItem 
          href="#subscribe" 
          $active={activeSection === 'subscribe'}
          onClick={(e) => {
            e.preventDefault();
            openNewsletterModal();
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          subscribe
        </NavItem>
      </NavigationBar>
      
      {/* Global Footer - Use Footer component directly like on home page */}
      <GlobalFooter />
      {/* PromptBar removed — GlobalChatSystem will be added later */}
      
      {/* Add toast notification for link copied */}
      <Notification show={showCopyNotification}>
        {notificationMessage}
      </Notification>

      {/* Newsletter Modal */}
      <NewsletterOverlay $open={isNewsletterModalOpen} onClick={() => setIsNewsletterModalOpen(false)}>
        <NewsletterModal onClick={(e) => e.stopPropagation()}>
          <NewsletterClose onClick={() => setIsNewsletterModalOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </NewsletterClose>
          <NewsletterModalTitle>subscribe</NewsletterModalTitle>
          <NewsletterModalText>
            get strategic insights for agent builders delivered to your inbox every week
          </NewsletterModalText>
          <NewsletterForm onSubmit={handleNewsletterSubmit}>
            <NewsletterInput
              type="text"
              placeholder="your name"
              value={newsletterName}
              onChange={(e) => setNewsletterName(e.target.value)}
              required
            />
            <NewsletterInput
              type="email"
              placeholder="your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
            />
            <NewsletterSubmit>
              subscribe
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </NewsletterSubmit>
          </NewsletterForm>
        </NewsletterModal>
      </NewsletterOverlay>
    </BlogContainer>
  );
};

export default Blog; 

